import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  flexRender,
  createColumnHelper,
} from '@tanstack/react-table';
import { 
  Users, 
  Search, 
  Filter, 
  AlertCircle,
  CheckCircle,
  XCircle,
  Edit,
  Save,
  X,
  RotateCcw,
  Loader,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  ArrowUpDown,
  LogIn,
  Eye,
  Mail,
  CreditCard,
  Copy,
  Check,
  Building2,
  ShieldCheck,
  ShieldAlert,
  Play,
  Square,
  Radar,
  Monitor,
  Globe,
  UserPlus,
  KeyRound,
  EyeOff,
  Sparkles,
  Layers,
  Power
} from 'lucide-react';
import { planRowLimit, parseRowLimitOverride, resolveRowLimit } from '../../services/planRowLimit';
import AgentModelsCard from './AgentModelsCard';

const API_BASE = process.env.REACT_APP_API_URL || 'https://mars2.mediacle.net';

const fmtDate = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

// Logins need the time of day, not just the date — two sign-ins on the same
// afternoon are the interesting case.
const fmtDateTime = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
  });
};

const fmtMoney = (amount, currency) => {
  if (amount === null || amount === undefined || amount === '') return '—';
  const n = Number(amount);
  if (Number.isNaN(n)) return '—';
  // Stripe reports minor units (cents).
  return `${(n / 100).toFixed(2)} ${String(currency || 'usd').toUpperCase()}`;
};

// Copy-to-clipboard chip for ids the admin actually needs to paste into Stripe.
const CopyField = ({ value, mono = true }) => {
  const [copied, setCopied] = useState(false);
  if (!value) return <span className="text-gray-400 dark:text-slate-500">—</span>;
  const copy = () => {
    navigator.clipboard?.writeText(String(value));
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <button
      onClick={copy}
      title="Click to copy"
      className={`group inline-flex items-center gap-1.5 max-w-full text-left ${mono ? 'font-mono text-xs' : 'text-sm'} text-gray-900 dark:text-slate-100 hover:text-blue-600 dark:hover:text-blue-400`}
    >
      <span className="truncate">{value}</span>
      {copied
        ? <Check className="w-3 h-3 shrink-0 text-green-600" />
        : <Copy className="w-3 h-3 shrink-0 opacity-0 group-hover:opacity-60" />}
    </button>
  );
};

const Field = ({ label, children }) => (
  <div className="min-w-0">
    <dt className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-slate-400">{label}</dt>
    <dd className="mt-0.5 text-sm text-gray-900 dark:text-slate-100 break-words">{children ?? '—'}</dd>
  </div>
);

// A demo account is meant to be handed to a prospect mid-conversation, so the
// form opens with everything already filled in: only the email is really
// required, and every default here is overridable before pressing Create.
const BLANK_CREATE_FORM = {
  businessEmail: '',
  firstName: 'Demo',
  lastName: 'Account',
  companyName: '',
  plan: 'Pro',
  password: '',
  credits: 100,
  brandLimit: 2,
  compLimit: 2,
  gapLimit: 2,
  emailLimit: 500,
  expiresInDays: 30,
  isDemo: true
};

// What actually gets pasted into an email or a chat window.
const loginBlock = (email, password) => `Email: ${email}\nPassword: ${password}`;

// Password / login chip: click to copy, with the "copied" tick the rest of the
// panel already uses. Monospaced because these get read aloud and retyped.
const SecretValue = ({ value, label = 'password' }) => {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard?.writeText(String(value));
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <button
      onClick={copy}
      title={`Click to copy ${label}`}
      className="group inline-flex items-center gap-1.5 max-w-full px-2 py-1 rounded-md bg-gray-100 dark:bg-slate-800 font-mono text-xs text-gray-900 dark:text-slate-100 hover:bg-gray-200 dark:hover:bg-slate-700"
    >
      <span className="truncate">{value}</span>
      {copied
        ? <Check className="w-3 h-3 shrink-0 text-green-600" />
        : <Copy className="w-3 h-3 shrink-0 opacity-40 group-hover:opacity-80" />}
    </button>
  );
};


// Row visibility is the one limit that used to be untunable: the plan decided
// it (Free 0 / Growth 25 / Pro 500 / Enterprise unlimited) and nothing else
// could, so widening one customer meant a code change. users.row_limit is the
// per-account override, the same shape as brand_limit / comp_limit / gap_limit.
//
// Blank means "no override". The column ships as the string 'normal' for every
// account, which means the same thing, so the box opens empty rather than
// showing a word an admin might mistake for a value.
const ROW_LIMIT_PRESETS = [
  { label: 'Plan default', value: '' },
  { label: '100', value: '100' },
  { label: '250', value: '250' },
  { label: '500', value: '500' },
  { label: '1000', value: '1000' },
  { label: 'Unlimited', value: 'unlimited' },
];

const fmtRowLimit = (n) => (n === Infinity ? 'unlimited' : `${n} rows`);

const RowLimitEditor = ({ user, onSave, saving }) => {
  const stored = parseRowLimitOverride(user?.row_limit) === null
    ? ''
    : String(user.row_limit).trim().toLowerCase();
  const [value, setValue] = useState(stored);

  const plan = user?.plan || 'Free';
  const planDefault = planRowLimit(plan);
  const typed = value.trim().toLowerCase();
  const invalid = typed !== '' && parseRowLimitOverride(typed) === null;
  const dirty = typed !== stored;
  const isOverride = typed !== '' && !invalid;
  const preview = resolveRowLimit(plan, isOverride ? typed : null);

  return (
    <div className="col-span-2">
      <dt className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-slate-400">
        Row limit
      </dt>
      <dd className="mt-1 flex flex-wrap items-center gap-2">
        <input
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder={`Plan default — ${fmtRowLimit(planDefault)}`}
          className="w-44 px-2 py-1 rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm text-gray-900 dark:text-slate-100"
        />
        <button
          onClick={() => onSave(typed === '' ? 'normal' : typed)}
          disabled={saving || invalid || !dirty}
          className="px-2.5 py-1 rounded-md text-xs font-medium bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
        {dirty && (
          <button
            onClick={() => setValue(stored)}
            disabled={saving}
            className="px-2.5 py-1 rounded-md text-xs text-gray-600 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-800"
          >
            Cancel
          </button>
        )}
      </dd>
      <div className="mt-1.5 flex flex-wrap gap-1">
        {ROW_LIMIT_PRESETS.map((preset) => (
          <button
            key={preset.label}
            onClick={() => setValue(preset.value)}
            disabled={saving}
            className={`px-2 py-0.5 rounded text-[11px] border ${
              typed === preset.value
                ? 'border-blue-500 text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-950/40'
                : 'border-gray-200 dark:border-slate-700 text-gray-600 dark:text-slate-400 hover:bg-gray-50 dark:hover:bg-slate-800'
            }`}
          >
            {preset.label}
          </button>
        ))}
      </div>
      <p className="mt-1 text-[11px] text-gray-500 dark:text-slate-400">
        {invalid
          ? 'Enter a number of rows, or "unlimited", or leave blank for the plan default.'
          : isOverride
            ? `Sees ${fmtRowLimit(preview)} — overrides the ${plan} default of ${fmtRowLimit(planDefault)}.`
            : `Sees ${fmtRowLimit(preview)} — the ${plan} default. No override set.`}
      </p>
      <p className="mt-0.5 text-[11px] text-gray-400 dark:text-slate-500">
        Takes effect on the customer&apos;s next page load.
      </p>
    </div>
  );
};

// Team seats: how many people (besides the owner) this account may invite.
// Plan default unless overridden — the same table the server uses in
// mars-backend/services/account/permissions.js (PLAN_SEATS). users.seat_limit
// takes the same shapes as row_limit: blank/'normal', a number, 'unlimited'.
const PLAN_SEATS = { free: 0, growth: 2, pro: 5, enterprise: 15 };

const SeatLimitEditor = ({ user, onSave, saving }) => {
  const raw = String(user?.seat_limit ?? '').trim().toLowerCase();
  const stored = raw === '' || raw === 'normal' ? '' : raw;
  const [value, setValue] = useState(stored);

  if (user?.account_owner_id) {
    return (
      <div className="col-span-2">
        <dt className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-slate-400">Team</dt>
        <dd className="mt-1 text-sm text-gray-700 dark:text-slate-300">
          {user.account_role === 'co_admin' ? 'Co-admin' : 'Member'} of account #{user.account_owner_id}.
          Their plan and limits are that account&apos;s.
        </dd>
      </div>
    );
  }

  const planDefault = PLAN_SEATS[String(user?.plan || 'free').toLowerCase()] ?? 0;
  const typed = value.trim().toLowerCase();
  const invalid = typed !== '' && typed !== 'unlimited' && !(Number.isInteger(Number(typed)) && Number(typed) > 0);
  const dirty = typed !== stored;

  return (
    <div className="col-span-2">
      <dt className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-slate-400">Team seats</dt>
      <dd className="mt-1 flex flex-wrap items-center gap-2">
        <input
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder={`Plan default — ${planDefault}`}
          className="w-44 px-2 py-1 rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm text-gray-900 dark:text-slate-100"
        />
        <button
          onClick={() => onSave(typed === '' ? 'normal' : typed)}
          disabled={saving || invalid || !dirty}
          className="px-2.5 py-1 rounded-md text-xs font-medium bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
      </dd>
      <p className="mt-1 text-[11px] text-gray-500 dark:text-slate-400">
        {invalid
          ? 'Enter a number of seats, or "unlimited", or leave blank for the plan default.'
          : typed
            ? `Can invite ${typed} people — overrides the ${user?.plan || 'Free'} default of ${planDefault}.`
            : `Can invite ${planDefault} people — the ${user?.plan || 'Free'} default.`}
      </p>
    </div>
  );
};

// One toggle row in the "Vertical access" section (see the drawer below).
// `item` is one entry from GET /api/user-panel/vertical-access/:id — { vertical,
// default, allowed, overridden, granted_by, granted_at }. Trading & Investment is
// rendered visually distinct since it's the opt-in one (default OFF); every
// gambling vertical is default ON and this is just a per-account revoke.
const VerticalAccessRow = ({ item, isForex, saving, onToggle }) => {
  const { vertical, allowed, overridden, default: def, granted_by, granted_at } = item;
  return (
    <div
      className={`flex items-center justify-between gap-3 px-3 py-2 rounded-lg border ${
        isForex
          ? 'border-purple-300 dark:border-purple-700 bg-purple-50 dark:bg-purple-950/30'
          : 'border-gray-200 dark:border-slate-700'
      }`}
    >
      <div className="min-w-0">
        <div className="flex items-center gap-1.5">
          {isForex && <Sparkles className="w-3.5 h-3.5 text-purple-600 dark:text-purple-400 shrink-0" />}
          <span className={`text-sm font-medium truncate ${isForex ? 'text-purple-800 dark:text-purple-200' : 'text-gray-800 dark:text-slate-200'}`}>
            {vertical}
          </span>
          {isForex && (
            <span className="px-1.5 py-0.5 rounded text-[9px] font-semibold bg-purple-200 dark:bg-purple-900 text-purple-800 dark:text-purple-200 uppercase tracking-wide shrink-0">
              Opt-in
            </span>
          )}
        </div>
        <div className="text-[11px] text-gray-500 dark:text-slate-400 mt-0.5">
          {overridden
            ? `Overridden${granted_by ? ` by ${granted_by}` : ''}${granted_at ? ` on ${fmtDate(granted_at)}` : ''} — default is ${def ? 'on' : 'off'}`
            : `Default (${def ? 'on' : 'off'}) — no override set`}
        </div>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={allowed}
        aria-label={`${vertical} ${allowed ? 'allowed' : 'denied'}`}
        disabled={saving}
        onClick={() => onToggle(vertical, !allowed)}
        className={`relative w-10 h-5 rounded-full transition-colors shrink-0 disabled:opacity-50 disabled:cursor-not-allowed ${
          allowed ? (isForex ? 'bg-purple-600' : 'bg-green-600') : 'bg-gray-300 dark:bg-slate-600'
        }`}
      >
        <span
          className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${
            allowed ? 'translate-x-5' : 'translate-x-0'
          }`}
        />
      </button>
    </div>
  );
};

const Section = ({ title, icon: Icon, children }) => (
  <div className="border-t border-gray-200 dark:border-slate-700 pt-4 mt-4 first:border-0 first:pt-0 first:mt-0">
    <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-slate-100 mb-3">
      {Icon && <Icon className="w-4 h-4 text-blue-600" />}
      {title}
    </h3>
    {children}
  </div>
);

// What each state a desktop can report means, said the way an operator would
// say it. `blocked` and `excluded` are the two that matter most: neither is a
// fault, and both would otherwise read as "stopped" and send somebody to fix a
// machine that is behaving exactly as it was set up to.
const MODULE_STATES = {
  running:  { label: 'running',  dot: 'bg-green-500' },
  starting: { label: 'starting', dot: 'bg-green-400 animate-pulse' },
  stopped:  { label: 'stopped',  dot: 'bg-gray-400' },
  finished: { label: 'finished', dot: 'bg-blue-400' },
  failed:   { label: 'failed',   dot: 'bg-red-500' },
  gaveup:   { label: 'gave up',  dot: 'bg-red-500' },
  blocked:  { label: 'cannot run here', dot: 'bg-amber-400' },
  excluded: { label: 'left out there',  dot: 'bg-amber-400' },
  unknown:  { label: 'unknown',  dot: 'bg-gray-300 dark:bg-slate-600' }
};

// One switchable thing, inside the card of the system it runs on.
//
// The switch writes what it SHOULD be doing; the line under it is what that
// machine last said it IS doing, up to a minute behind. Those are two different
// facts and the row keeps them visibly apart — a switch that looked like a live
// state would read as broken for the minute after every click.
const SystemModuleRow = ({ row, saving, onToggle }) => {
  const look = MODULE_STATES[row.state] || null;

  return (
    <div className="flex items-start justify-between gap-3 py-2">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm text-gray-800 dark:text-slate-200">{row.label}</span>
          <span className={`px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase tracking-wide ${
            row.kind === 'batch'
              ? 'bg-indigo-100 text-indigo-800 dark:bg-indigo-950/60 dark:text-indigo-300'
              : row.kind === 'cron'
                ? 'bg-purple-100 text-purple-800 dark:bg-purple-950/60 dark:text-purple-300'
                : 'bg-gray-100 text-gray-700 dark:bg-slate-800 dark:text-slate-300'
          }`}>
            {row.kind}
          </span>
          {/* A machine set differently from the fleet is the one thing about a
              switch that is invisible from the switch itself. */}
          {row.overridden && (
            <span
              title="Set on this machine only — it does not follow the All desktops switch"
              className="px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase tracking-wide bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300"
            >
              only here
            </span>
          )}
        </div>

        <div className="flex items-center gap-1.5 text-[11px] text-gray-500 dark:text-slate-400 mt-0.5">
          {look && <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${look.dot}`} />}
          <span className="truncate" title={row.detail || row.note}>
            {/* The machine's own words first when it has any — "last ran 12s
                ago" answers the question the schedule only implies. */}
            {row.detail || row.note}
          </span>
        </div>

        {/* A batch has an end, so "on" is not a state it stays in — it is a
            trigger. Saying so here is cheaper than the support question. */}
        {row.enabled && row.kind === 'batch' && (
          <p className="text-[11px] text-gray-400 dark:text-slate-500 mt-0.5">
            switching this off and on again is how you run it
          </p>
        )}
        {!row.enabled && (row.updatedBy || row.updatedAt) && (
          <p className="text-[11px] text-gray-400 dark:text-slate-500 mt-0.5">
            switched off{row.updatedBy ? ` by ${row.updatedBy}` : ''}{row.updatedAt ? ` on ${fmtDate(row.updatedAt)}` : ''}
          </p>
        )}
      </div>

      <button
        type="button"
        role="switch"
        aria-checked={row.enabled}
        aria-label={`${row.label} ${row.enabled ? 'on' : 'off'}`}
        disabled={saving}
        onClick={() => onToggle(row.key, !row.enabled, row.machine)}
        className={`relative w-10 h-5 mt-1 rounded-full transition-colors shrink-0 disabled:opacity-50 disabled:cursor-not-allowed ${
          row.enabled ? 'bg-green-600' : 'bg-gray-300 dark:bg-slate-600'
        }`}
      >
        <span
          className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${
            row.enabled ? 'translate-x-5' : 'translate-x-0'
          }`}
        />
      </button>
    </div>
  );
};

// One system — a machine, or the fleet default the machines follow — with
// everything it can be told to run or not run inside it.
//
// This used to be the other way round: a row per script, listing the machines
// under it. That reads well with one machine and badly with four, because the
// same box is then drawn once per thing it runs, and the question anybody
// actually arrives with is "what is this machine doing", not "where does this
// script run".
const SystemCard = ({ system, saving, onToggle }) => (
  <div className={`rounded-lg border ${
    system.online
      ? 'border-gray-200 dark:border-slate-700'
      : 'border-dashed border-gray-300 dark:border-slate-700'
  }`}>
    <div className={`px-4 py-2.5 border-b border-gray-200 dark:border-slate-700 ${
      system.online ? '' : 'opacity-60'
    }`}>
      <div className="flex items-center gap-2">
        <system.icon className={`w-4 h-4 shrink-0 ${system.online ? 'text-green-600' : 'text-gray-400'}`} />
        <span className="text-sm font-semibold text-gray-900 dark:text-slate-100 truncate" title={system.machine || undefined}>
          {system.title}
        </span>
        {system.badge && (
          <span className="px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase tracking-wide bg-blue-100 text-blue-800 dark:bg-blue-950/60 dark:text-blue-300">
            {system.badge}
          </span>
        )}
      </div>
      <p className="text-[11px] text-gray-500 dark:text-slate-400 mt-0.5">{system.subtitle}</p>
    </div>

    <div className="px-4 py-1 divide-y divide-gray-100 dark:divide-slate-800">
      {system.rows.map((row) => (
        <SystemModuleRow
          key={row.key}
          row={row}
          saving={Boolean(saving[`${row.machine || '*'}:${row.key}`])}
          onToggle={onToggle}
        />
      ))}
    </div>
  </div>
);

const UserPanel = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [globalFilter, setGlobalFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [planFilter, setPlanFilter] = useState('all');
  const [subFilter, setSubFilter] = useState('all');
  const [editingUser, setEditingUser] = useState(null);
  const [updating, setUpdating] = useState(false);
  // Customer detail drawer
  const [detailUser, setDetailUser] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(null);
  // Per-account vertical overrides for the open detail drawer — see
  // services/verticalAccess/verticalAccess.js on the backend. Keyed to
  // whichever user the drawer is open for; reset on close/re-open so a stale
  // fetch never renders against the wrong account.
  const [verticalAccess, setVerticalAccess] = useState(null);
  const [verticalAccessLoading, setVerticalAccessLoading] = useState(false);
  const [verticalAccessError, setVerticalAccessError] = useState(null);
  const [verticalAccessSaving, setVerticalAccessSaving] = useState({}); // { [vertical]: true } while its PUT is in flight
  // Live affiliate check — the desktop fleet. The panel queues work and reads
  // back what happened; the machines themselves poll the job table and decide
  // nothing here.
  const [queue, setQueue] = useState({ users: [], workers: [], fleet: null });
  const [queueBusy, setQueueBusy] = useState(null);
  const [queueNotice, setQueueNotice] = useState(null);
  // The desktop switchboard: which of the seven scripts should be running, and
  // what each machine says it is actually doing about that. Separate from
  // `queue` above even though both are about the same desktops — that one is
  // the live-check job queue, this one is the scripts themselves, and a machine
  // can be perfectly healthy on one and switched off on the other.
  const [desktop, setDesktop] = useState(null);
  const [desktopSaving, setDesktopSaving] = useState({}); // { [moduleKey]: true } while its PUT is in flight
  const [desktopNotice, setDesktopNotice] = useState(null);
  // Lost affiliate monitoring — the OTHER fleet. Same job table, same worker,
  // but scope='live': a brand's own affiliates rather than all 44k rows, and
  // the machines that take it run mars-weekly-lost-affiliate rather than
  // mars-live-affiliate-checker. Kept as its own piece of state because the two
  // queues can be healthy and starved independently of each other.
  const [monitor, setMonitor] = useState(null);
  const [monitorBusy, setMonitorBusy] = useState(false);
  const [monitorNotice, setMonitorNotice] = useState(null);
  const [monitorOpen, setMonitorOpen] = useState(false);
  // Instant (demo) accounts: the create form, and the passwords revealed so far.
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState(BLANK_CREATE_FORM);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState(null);
  const [created, setCreated] = useState(null);
  // Revealed passwords, keyed by user id. Fetched per row rather than shipped
  // with the list, so a plaintext password only exists in the page once an
  // admin has asked for that specific account.
  const [revealed, setRevealed] = useState({});
  const [revealBusy, setRevealBusy] = useState(null);
  const [demoFilter, setDemoFilter] = useState('all');

  // Plan limits configuration
  const planLimits = {
    Free: { brand: 0, comp: 0, gap: 0, contact: 0 },
    Growth: { brand: 1, comp: 1, gap: 1, contact: 100 },
    Pro: { brand: 2, comp: 2, gap: 2, contact: 500 },
    Enterprise: { brand: 5, comp: 5, gap: 5, contact: 'unlimited' }
  };

  const planOptions = ['Free', 'Growth', 'Pro', 'Enterprise'];
  const statusOptions = ['active', 'inactive', 'suspended'];

  // brand_limit / comp_limit / gap_limit / email_limit are per-account overrides.
  // When one is set it wins over the plan defaults above; otherwise fall back.
  const effectiveLimits = (user) => {
    const base = planLimits[user.plan] || planLimits.Free;
    const pick = (override, fallback) =>
      (override === null || override === undefined || override === '') ? fallback : Number(override);
    return {
      brand: pick(user.brand_limit, base.brand),
      comp: pick(user.comp_limit, base.comp),
      gap: pick(user.gap_limit, base.gap),
      contact: pick(user.email_limit, base.contact)
    };
  };

  const columnHelper = createColumnHelper();

  // Fetch users data
  useEffect(() => {
    fetchUsersData();
  }, []);

  const fetchUsersData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'https://mars2.mediacle.net'}/api/user-panel`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });


      if (response.ok) {
        const result = await response.json();
        
        if (result.success && Array.isArray(result.data)) {
          // Ensure all user objects have required properties with defaults
          const processedUsers = result.data.map(user => ({
            id: user.id || Math.random(), // Fallback ID if missing
            first_name: user.first_name || '',
            last_name: user.last_name || '',
            user_status: user.user_status || 'inactive',
            plan: user.plan || 'Free',
            contact_finder_count: parseInt(user.contact_finder_count) || 0,
            total_brand: parseInt(user.total_brand) || 0,
            total_comp: parseInt(user.total_comp) || 0,
            total_gap: parseInt(user.total_gap) || 0,
            email_limit: parseInt(user.email_limit) || 0,
            business_email: user.business_email || '',
            updated_at: user.updated_at || null,
            company_name: user.company_name || '',
            phone_number: user.phone_number || '',
            country: user.country || '',
            email_verify: user.email_verify === true || user.email_verify === 'true',
            auth_provider: user.auth_provider || 'email',
            credits: parseInt(user.credits) || 0,
            brand_limit: user.brand_limit,
            comp_limit: user.comp_limit,
            gap_limit: user.gap_limit,
            row_limit: user.row_limit,
            plan_started_date: user.plan_started_date || null,
            plan_expiry: user.plan_expiry || null,
            subscription_status: user.subscription_status || '',
            stripe_customer_id: user.stripe_customer_id || '',
            stripe_subscription_id: user.stripe_subscription_id || '',
            subscription_start_date: user.subscription_start_date || null,
            subscription_end_date: user.subscription_end_date || null,
            trial_end_date: user.trial_end_date || null,
            // Admin-created instant login. Only these rows have a password that
            // can be shown again — everyone else's is a one-way hash.
            is_demo: user.is_demo === true || user.is_demo === 'true',
            demo_created_by: user.demo_created_by || '',
            demo_created_at: user.demo_created_at || null
          }));
          
          setUsers(processedUsers);
        } else {
          console.error('Invalid data structure:', result);
          setError(result.message || 'Invalid data received from server');
        }
      } else {
        const errorText = await response.text();
        console.error('Response error:', errorText);
        setError(`Failed to fetch users: ${response.status} - ${response.statusText}`);
      }
    } catch (error) {
      console.error('Network error:', error);
      setError(`Network error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const updateUser = async (userId, updateData) => {
    try {
      setUpdating(true);
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'https://mars2.mediacle.net'}/api/user-panel/update`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          userId,
          ...updateData
        })
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setUsers(prevUsers => 
            prevUsers.map(user => 
              user.id === userId ? { ...user, ...updateData } : user
            )
          );
          // The detail drawer renders from its own copies of the row
          // (`detailUser` from the list, `detail.user` from the fetch), so
          // both have to move too or a saved value reads as unsaved until
          // the panel is reloaded.
          setDetailUser(prev =>
            prev && prev.id === userId ? { ...prev, ...updateData } : prev
          );
          setDetail(prev =>
            prev && prev.user && prev.user.id === userId
              ? { ...prev, user: { ...prev.user, ...updateData } }
              : prev
          );
          setEditingUser(null);
        } else {
          alert('Failed to update user: ' + result.message);
        }
      } else {
        alert('Failed to update user');
      }
    } catch (error) {
      console.error('Error updating user:', error);
      alert('Network error occurred while updating user');
    } finally {
      setUpdating(false);
    }
  };

  // Create an account that works immediately: verified, active, and on a plan
  // with real limits. Nothing is emailed — the credentials come back in this
  // response for the admin to pass on however they like.
  const createAccount = async (event) => {
    event?.preventDefault?.();
    setCreating(true);
    setCreateError(null);
    try {
      const response = await fetch(`${API_BASE}/api/user-panel/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(createForm)
      });
      const result = await response.json().catch(() => ({}));
      if (response.ok && result.success) {
        setCreated(result.data);
        setShowCreate(false);
        setCreateForm(BLANK_CREATE_FORM);
        // The new row has to come from the server: it carries the generated id,
        // dates and demo flags that the form never knew about.
        await fetchUsersData();
      } else {
        setCreateError(result.message || `Failed to create account (${response.status})`);
      }
    } catch (err) {
      setCreateError(`Network error: ${err.message}`);
    } finally {
      setCreating(false);
    }
  };

  // Read back the password of an account created here. Only demo accounts have
  // one stored; for anyone else the server says so and this shows that reason
  // rather than an empty field.
  const revealPassword = async (userId) => {
    if (revealed[userId]) {
      setRevealed((prev) => {
        const next = { ...prev };
        delete next[userId];
        return next;
      });
      return;
    }
    setRevealBusy(userId);
    try {
      const response = await fetch(`${API_BASE}/api/user-panel/credentials/${userId}`, {
        credentials: 'include'
      });
      const result = await response.json().catch(() => ({}));
      setRevealed((prev) => ({
        ...prev,
        [userId]: response.ok && result.success
          ? result.data
          : { error: result.message || `Could not read credentials (${response.status})` }
      }));
    } catch (err) {
      setRevealed((prev) => ({ ...prev, [userId]: { error: `Network error: ${err.message}` } }));
    } finally {
      setRevealBusy(null);
    }
  };

  const resetUserCounts = async (userId, field) => {
    const resetData = { [field]: 0 };
    await updateUser(userId, resetData);
  };

  // Open the detail drawer. The row we already have renders immediately so the
  // panel is never blank, then the full record replaces it when it lands.
  const openDetail = async (user) => {
    setDetailUser(user);
    setDetail(null);
    setDetailError(null);
    setDetailLoading(true);
    fetchVerticalAccess(user.id);
    try {
      const response = await fetch(`${API_BASE}/api/user-panel/user/${user.id}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include'
      });
      const result = await response.json();
      if (response.ok && result.success) {
        setDetail(result.data);
      } else {
        setDetailError(result.message || `Failed to load customer (${response.status})`);
      }
    } catch (err) {
      setDetailError(`Network error: ${err.message}`);
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => {
    setDetailUser(null);
    setDetail(null);
    setDetailError(null);
    setVerticalAccess(null);
    setVerticalAccessError(null);
    setVerticalAccessSaving({});
  };

  // GET /api/user-panel/vertical-access/:id — the default+override merged view
  // for the "Vertical access" section of the drawer.
  const fetchVerticalAccess = async (userId) => {
    setVerticalAccessLoading(true);
    setVerticalAccessError(null);
    try {
      const response = await fetch(`${API_BASE}/api/user-panel/vertical-access/${userId}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include'
      });
      const result = await response.json();
      if (response.ok && result.success) {
        setVerticalAccess(result.data);
      } else {
        setVerticalAccessError(result.message || `Failed to load vertical access (${response.status})`);
      }
    } catch (err) {
      setVerticalAccessError(`Network error: ${err.message}`);
    } finally {
      setVerticalAccessLoading(false);
    }
  };

  // PUT /api/user-panel/vertical-access — optimistic toggle with rollback on
  // failure, so a flaky request never leaves the switch showing something the
  // server didn't actually save.
  const toggleVerticalAccess = async (userId, vertical, nextAllowed) => {
    const previous = verticalAccess;
    setVerticalAccess((prev) => prev ? {
      ...prev,
      verticals: prev.verticals.map((v) =>
        v.vertical === vertical ? { ...v, allowed: nextAllowed, overridden: true } : v
      )
    } : prev);
    setVerticalAccessSaving((prev) => ({ ...prev, [vertical]: true }));

    try {
      const response = await fetch(`${API_BASE}/api/user-panel/vertical-access`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ userId, vertical, allowed: nextAllowed })
      });
      const result = await response.json();
      if (!response.ok || !result.success) {
        throw new Error(result.message || `Failed to update ${vertical}`);
      }
      // Re-fetch rather than trust the optimistic `overridden: true` guess —
      // the backend deletes the override row instead of storing it when the
      // new value just matches the code default, and only the server knows
      // which case this was.
      fetchVerticalAccess(userId);
      // Keep the list row's Forex badge in sync without a full reload.
      if (vertical === 'Trading & Investment') {
        setUsers((prevUsers) => prevUsers.map((u) => u.id === userId ? { ...u, forex_access: nextAllowed } : u));
      }
    } catch (err) {
      console.error('Vertical access update error:', err);
      alert(`Failed to update ${vertical}: ${err.message}`);
      setVerticalAccess(previous); // rollback
    } finally {
      setVerticalAccessSaving((prev) => {
        const next = { ...prev };
        delete next[vertical];
        return next;
      });
    }
  };

  // "Login as" — swap the admin session over to this user, then hard-reload as
  // them so all app/Redux state resets. Return to admin via the sidebar banner.
  const impersonateUser = async (userId, name) => {
    if (!window.confirm(
      `Sign in as ${name || 'this user'}?\n\nYou'll see the app exactly as they do. Use "Return to admin" in the sidebar to switch back.`
    )) {
      return;
    }
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'https://mars2.mediacle.net'}/api/user-panel/impersonate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ userId })
      });
      const result = await response.json();
      if (response.ok && result.success) {
        window.location.href = '/dashboard';
      } else {
        alert('Failed to sign in as user: ' + (result.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('Impersonate error:', error);
      alert('Network error occurred while switching account');
    }
  };

  // --- live affiliate check (the desktop fleet) -----------------------------

  const fetchQueue = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/api/local-affiliate-check/queue`, {
        credentials: 'include'
      });
      const result = await response.json();
      if (result.success) {
        setQueue({ users: result.users || [], workers: result.workers || [], fleet: result.fleet || null });
      }
    } catch {
      // Transient. The next poll picks it up, and a failed poll must not blank
      // out the fleet strip — a machine that looks offline for one bad request
      // is exactly the wrong thing to show.
    }
  }, []);

  useEffect(() => { fetchQueue(); }, [fetchQueue]);

  const fleetBusy = ((queue.fleet?.running || 0) + (queue.fleet?.queued || 0)) > 0;

  // Sweeps run for hours, so an idle panel has nothing to see. Poll fast only
  // while something is actually in flight.
  useEffect(() => {
    const timer = setInterval(fetchQueue, fleetBusy ? 8000 : 30000);
    return () => clearInterval(timer);
  }, [fleetBusy, fetchQueue]);

  // --- desktop scripts (the switchboard) ------------------------------------

  const fetchDesktop = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/api/desktop-control/state`, { credentials: 'include' });
      const result = await response.json();
      if (result.success) setDesktop(result);
    } catch {
      // Same reasoning as fetchQueue: a failed poll must not blank the card.
      // A desktop that looks like it vanished because one request timed out is
      // exactly the wrong thing to show somebody.
    }
  }, []);

  useEffect(() => { fetchDesktop(); }, [fetchDesktop]);

  // The machines report once a minute, so polling faster than that only shows
  // the same answer again — except right after a click, which is what the
  // optimistic update below covers.
  useEffect(() => {
    const timer = setInterval(fetchDesktop, 20000);
    return () => clearInterval(timer);
  }, [fetchDesktop]);

  // `machine` null means the fleet default that every desktop follows; a
  // machine id means that box alone. The backend already resolves the two —
  // machine row beats fleet row beats a default of on — and deletes a machine
  // row that has stopped disagreeing, which is why the optimistic update below
  // has to recompute `overridden` rather than just setting it.
  const toggleModule = async (key, enabled, machine = null) => {
    const savingKey = `${machine || '*'}:${key}`;
    setDesktopSaving((prev) => ({ ...prev, [savingKey]: true }));
    setDesktopNotice(null);
    try {
      const response = await fetch(`${API_BASE}/api/desktop-control/module`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ module: key, enabled, ...(machine ? { machine } : {}) })
      });
      const result = await response.json();
      if (response.ok && result.success) {
        // Move the switch now and say when it will take effect. The machines
        // find out on their next check-in, so re-fetching immediately would
        // paint the old state back for up to a minute and read as a failed
        // click; the poll above corrects this if the write did not stick.
        setDesktop((prev) => prev && {
          ...prev,
          modules: prev.modules.map((m) => {
            if (m.key !== key) return m;
            if (machine) {
              // One box. It is an override only while it still differs from
              // the fleet; agreeing with the fleet clears the row server-side.
              return {
                ...m,
                machines: (m.machines || []).map((row) => (
                  row.machine === machine
                    ? { ...row, enabled, overridden: enabled !== m.enabled }
                    : row
                ))
              };
            }
            // The fleet. Every machine that was not deliberately set the other
            // way moves with it, and any that was now agrees and is no longer
            // an override.
            return {
              ...m,
              enabled,
              source: 'fleet',
              machines: (m.machines || []).map((row) => (
                row.overridden && row.enabled !== enabled
                  ? row
                  : { ...row, enabled, overridden: false }
              ))
            };
          })
        });
        setDesktopNotice({ ok: true, text: result.message });
      } else {
        setDesktopNotice({ ok: false, text: result.message || 'Could not change that' });
        fetchDesktop();
      }
    } catch {
      setDesktopNotice({ ok: false, text: 'Network error — nothing was changed' });
    } finally {
      setDesktopSaving((prev) => { const next = { ...prev }; delete next[savingKey]; return next; });
    }
  };

  // System control, assembled one card per system instead of one row per
  // script. The API answers module-first — every script, with the machines
  // running it underneath — which reads well with one machine and badly with
  // four, because the same box then gets drawn once per thing it runs. This
  // turns it inside out: the question anybody actually arrives with is "what is
  // this machine doing", not "where does this script run".
  //
  // The first card is not a machine. The seven desktop scripts have a
  // fleet-wide answer that exists before any desktop has said anything, and it
  // is the only place those switches can live while no box has checked in —
  // which is the state this panel is in whenever somebody opens it to find out
  // why nothing is running.
  const systems = useMemo(() => {
    if (!desktop) return [];
    const modules = desktop.modules || [];
    const machines = desktop.machines || [];
    const cards = [];

    const fleetModules = modules.filter((m) => (m.scope || 'desktop') !== 'server');
    if (fleetModules.length) {
      const boxes = machines.filter((m) => (m.scope || 'desktop') !== 'server');
      const up = boxes.filter((m) => m.online).length;
      cards.push({
        id: '*',
        machine: null,
        icon: Layers,
        title: 'All desktops',
        badge: 'default',
        online: true,
        subtitle: boxes.length
          ? `what every desktop follows unless set otherwise · ${up} of ${boxes.length} checked in · a change reaches them within a minute`
          : 'what every desktop follows · no desktop has checked in yet, so none of these is running anywhere',
        rows: fleetModules.map((mod) => ({
          key: mod.key,
          machine: null,
          label: mod.label,
          kind: mod.kind,
          note: mod.note,
          detail: null,
          state: null,
          enabled: mod.enabled,
          overridden: false,
          updatedBy: mod.updatedBy,
          updatedAt: mod.updatedAt
        }))
      });
    }

    // Then the machines, each holding only what it actually reports. A desktop
    // that cannot run a script never reports it, so its card does not offer a
    // switch for something switching would not start. Desktops first, the
    // backend last, so the list does not reorder itself as machines come and go.
    const ordered = [...machines].sort((a, b) => {
      const as = (a.scope || 'desktop') === 'server' ? 1 : 0;
      const bs = (b.scope || 'desktop') === 'server' ? 1 : 0;
      if (as !== bs) return as - bs;
      return String(a.hostname).localeCompare(String(b.hostname));
    });

    for (const box of ordered) {
      const server = (box.scope || 'desktop') === 'server';
      const rows = [];
      for (const mod of modules) {
        const seen = (mod.machines || []).find((m) => m.machine === box.machine);
        if (!seen) continue;
        rows.push({
          key: mod.key,
          machine: box.machine,
          label: mod.label,
          kind: mod.kind,
          note: mod.note,
          detail: seen.detail,
          state: seen.state,
          enabled: seen.enabled,
          overridden: seen.overridden,
          updatedBy: mod.updatedBy,
          updatedAt: mod.updatedAt
        });
      }
      if (!rows.length) continue;

      cards.push({
        id: box.machine,
        machine: box.machine,
        icon: server ? Globe : Monitor,
        // Deliberately not the hostname for the backend: that is a container id
        // and it changes on every deploy, so showing it dates the card without
        // telling anyone anything. When it started does tell them something —
        // it is how you see whether a deploy actually landed.
        title: server ? 'The server' : box.hostname,
        badge: server ? 'backend' : null,
        online: box.online,
        subtitle: !box.online
          ? `not checking in — last heard ${fmtDateTime(box.reportedAt)}, so it is running whatever it was last told`
          : server
            ? `schedules inside the API process · up since ${fmtDateTime(box.startedAt)} · a change takes effect at the next tick`
            : `reported ${fmtDateTime(box.reportedAt)} · a change reaches it within a minute`,
        rows
      });
    }

    return cards;
  }, [desktop]);

  const queueByUser = useMemo(() => {
    const map = new Map();
    for (const entry of queue.users || []) {
      if (entry.userId) map.set(entry.userId, entry);
    }
    return map;
  }, [queue.users]);

  // Who is waiting, in the order a freed desktop will actually take them: the
  // customer with the fewest brands running goes first, and within that the
  // one who has been waiting longest. `queue.users` already arrives ordered by
  // queue time, and Array.sort is stable, so one sort key reproduces the
  // server's decision.
  const waitingCustomers = useMemo(
    () => (queue.users || []).filter((u) => u.queued > 0).slice().sort((a, b) => a.running - b.running),
    [queue.users]
  );

  const userLabel = (user) =>
    `${user.first_name || ''} ${user.last_name || ''}`.trim() ||
    user.company_name || user.business_email || `user ${user.id}`;

  const runUserCheck = async (user) => {
    const entry = queueByUser.get(user.id);
    const inFlight = (entry?.queued || 0) + (entry?.running || 0);
    const name = userLabel(user);
    const stopping = inFlight > 0;
    const online = queue.fleet?.online || 0;

    const confirmed = stopping
      ? window.confirm(
          `Stop the live affiliate check for ${name}?\n\n` +
          `${entry.running} running and ${entry.queued} queued brand${inFlight === 1 ? '' : 's'} will be stopped. ` +
          `Everything already checked is kept — a re-run resumes from the cache rather than starting over.`
        )
      : window.confirm(
          `Queue a live affiliate check for every brand ${name} holds?\n\n` +
          `Each brand is a full pass over the affiliate table and takes hours. ` +
          (online
            ? `${online} desktop${online === 1 ? ' is' : 's are'} online, so ${online} run at a time — ` +
              `and any other customer waiting still gets a machine before this one gets a second.`
            : `No desktop is online right now, so nothing will actually start: the brands wait in the queue ` +
              `until one of the machines is running.`)
        );
    if (!confirmed) return;

    setQueueBusy(user.id);
    setQueueNotice(null);
    try {
      const response = await fetch(
        `${API_BASE}/api/local-affiliate-check/user/${stopping ? 'stop' : 'start'}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ userId: user.id })
        }
      );
      const result = await response.json().catch(() => ({}));
      setQueueNotice({ ...result, name, ok: !!result.success });
      await fetchQueue();
    } catch (err) {
      setQueueNotice({ ok: false, name, message: `Network error: ${err.message}` });
    } finally {
      setQueueBusy(null);
    }
  };

  // --- lost affiliate monitoring (the weekly fleet) -------------------------

  // Which queue a desktop polls. Two fleets share this job table now, and one
  // online count across both is the wrong number: "two desktops are up" is true
  // and useless while the queue you just filled has nobody on it.
  const FLEET_LABEL = { all: 'Brand checks', live: 'Lost affiliates' };
  const workerFleet = (worker) =>
    worker.scopes?.length ? worker.scopes.map((s) => FLEET_LABEL[s] || s).join(' + ') : null;

  // One card per machine, not per worker process. MED-CPU-008 runs three
  // checkers — brand checks, lost affiliates and trading — and each registers
  // its own worker id, so this drew the same desktop three times and left the
  // reader to work out that "three cards" was one box. Group by hostname and
  // the card answers what that machine is doing, which is the question anyone
  // is actually asking. A machine counts as online while any of its checkers
  // is, and each job in flight keeps its own bar: three checkers can be working
  // three brands at once and collapsing that would hide two of them.
  const fleetMachines = useMemo(() => {
    const byHost = new Map();
    for (const worker of queue.workers || []) {
      const id = worker.hostname || worker.workerId;
      if (!byHost.has(id)) byHost.set(id, { id, hostname: id, workers: [] });
      byHost.get(id).workers.push(worker);
    }
    return [...byHost.values()]
      .map((box) => ({
        ...box,
        online: box.workers.some((w) => w.online),
        busy: box.workers.filter((w) => w.jobId),
        lastSeen: box.workers.reduce(
          (latest, w) => (!latest || new Date(w.lastSeen) > new Date(latest) ? w.lastSeen : latest),
          null
        )
      }))
      .sort((a, b) => String(a.hostname).localeCompare(String(b.hostname)));
  }, [queue.workers]);

  const fetchMonitor = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/api/live-affiliate-monitor/admin/overview`, {
        credentials: 'include'
      });
      const result = await response.json();
      if (result.success) setMonitor(result);
    } catch {
      // Same reasoning as fetchQueue: one failed poll must not blank the card.
    }
  }, []);

  useEffect(() => { fetchMonitor(); }, [fetchMonitor]);

  const monitorBusyNow = (monitor?.totals?.inFlight || 0) > 0;

  useEffect(() => {
    const timer = setInterval(fetchMonitor, monitorBusyNow ? 8000 : 30000);
    return () => clearInterval(timer);
  }, [monitorBusyNow, fetchMonitor]);

  // Queue a monitor run for every monitored brand on every account — the Monday
  // cron, on demand. Cheap by design: each job reads only that brand's own
  // affiliates, so twelve brands is a few thousand pages, not half a million.
  const runMonitorAll = async () => {
    const totals = monitor?.totals || {};
    const liveOnline = monitor?.fleet?.liveOnline || 0;
    const liveDeclared = monitor?.fleet?.liveDeclared || 0;

    if (!totals.brands) {
      setMonitorNotice({
        ok: false,
        message: 'No brand has Lost Affiliate Monitoring switched on, so there is nothing to queue. ' +
                 'The toggle is on the brand card, per brand.'
      });
      return;
    }

    const confirmed = window.confirm(
      `Check every monitored brand for lost affiliates?\n\n` +
      `${totals.brands} brand${totals.brands === 1 ? '' : 's'} across ${totals.accounts} account${totals.accounts === 1 ? '' : 's'} ` +
      `will be queued. Each one re-reads only its own affiliates — minutes per brand, not the hours a full check takes.` +
      (totals.inFlight ? `\n\n${totals.inFlight} already in flight and will be left alone.` : '') +
      // Three states, not two. "Something will claim it" is not the same as
      // "the right thing will claim it", and the middle case is the one worth
      // stopping for: the run starts, looks fine, and takes an afternoon per
      // brand because the machine is on a checkout from before the split.
      (liveDeclared
        ? `\n\n${liveDeclared} desktop${liveDeclared === 1 ? ' is' : 's are'} on the monitor queue.`
        : liveOnline
        ? `\n\nWARNING: no desktop has declared the monitor queue. The work would be picked up by a ` +
          `checkout that predates the fleet split, which scans the whole affiliate table for every ` +
          `brand — hours each instead of minutes. Run git pull on that machine first.`
        : `\n\nNo desktop is on the monitor queue right now, so nothing will start: the brands wait until ` +
          `someone runs mars-weekly-lost-affiliate.`)
    );
    if (!confirmed) return;

    setMonitorBusy(true);
    setMonitorNotice(null);
    try {
      const response = await fetch(`${API_BASE}/api/live-affiliate-monitor/admin/queue-all`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include'
      });
      const result = await response.json().catch(() => ({}));
      setMonitorNotice({ ...result, ok: !!result.success });
      if (result.overview?.success) setMonitor(result.overview);
      else await fetchMonitor();
      setMonitorOpen(true);
    } catch (err) {
      setMonitorNotice({ ok: false, message: `Network error: ${err.message}` });
    } finally {
      setMonitorBusy(false);
    }
  };

  const getProgressPercentage = (current, limit) => {
    if (limit === 'unlimited' || limit === 0) return 0;
    return Math.min((current / limit) * 100, 100);
  };

  const getProgressColor = (percentage) => {
    if (percentage >= 90) return 'bg-red-500';
    if (percentage >= 70) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  const startEditing = (user) => {
    setEditingUser({
      id: user.id,
      user_status: user.user_status,
      plan: user.plan
    });
  };

  const cancelEditing = () => {
    setEditingUser(null);
  };

  const saveUser = () => {
    if (editingUser) {
      updateUser(editingUser.id, {
        user_status: editingUser.user_status,
        plan: editingUser.plan
      });
    }
  };

  // Progress Bar Component
  const ProgressBar = ({ label, current, limit, userId, field }) => (
    <div className="flex items-center gap-2 mb-1">
      <span className="text-xs text-gray-500 w-12">{label}:</span>
      <div className="flex-1 bg-gray-200 rounded-full h-2">
        <div 
          className={`h-2 rounded-full ${getProgressColor(getProgressPercentage(current, limit))}`}
          style={{width: `${getProgressPercentage(current, limit)}%`}}
        ></div>
      </div>
      <span className="text-xs text-gray-600 w-16">
        {current}/{limit === 'unlimited' ? '∞' : limit}
      </span>
      <button
        onClick={() => resetUserCounts(userId, field)}
        className="text-red-500 hover:text-red-700 p-1"
        title={`Reset ${label} Count`}
      >
        <RotateCcw className="w-3 h-3" />
      </button>
    </div>
  );

  // Define columns
  const columns = useMemo(() => [
    columnHelper.accessor('first_name', {
      id: 'user',
      header: 'User',
      cell: ({ row }) => {
        const user = row.original;
        if (!user) return <div>N/A</div>;
        
        return (
          <div className="flex items-center">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
              <span className="text-blue-600 font-medium text-sm">
                {(user.first_name || 'U').charAt(0).toUpperCase()}
                {(user.last_name || '').charAt(0).toUpperCase()}
              </span>
            </div>
            <div className="ml-4 min-w-0">
              <div className="flex items-center gap-1.5 min-w-0">
                <span className="text-sm font-medium text-gray-900 dark:text-slate-100 truncate">
                  {`${user.first_name || ''} ${user.last_name || ''}`.trim() || '(no name)'}
                </span>
                {user.is_demo && (
                  <span
                    className="shrink-0 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-medium text-amber-800 bg-amber-100 dark:text-amber-300 dark:bg-amber-900/40"
                    title={`Demo account created ${fmtDate(user.demo_created_at)}${user.demo_created_by ? ` by ${user.demo_created_by}` : ''}`}
                  >
                    <Sparkles className="w-2.5 h-2.5" />demo
                  </span>
                )}
                {user.forex_access && (
                  <span
                    className="shrink-0 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-medium text-purple-800 bg-purple-100 dark:text-purple-300 dark:bg-purple-900/40"
                    title="Trading & Investment vertical granted — see Vertical access in the detail drawer"
                  >
                    <Layers className="w-2.5 h-2.5" />Forex
                  </span>
                )}
              </div>
              {user.company_name && (
                <div className="text-xs text-gray-500 dark:text-slate-400 truncate flex items-center gap-1">
                  <Building2 className="w-3 h-3 shrink-0" />{user.company_name}
                </div>
              )}
            </div>
          </div>
        );
      },
    }),
    columnHelper.accessor('business_email', {
      id: 'email',
      header: 'Email',
      cell: ({ row }) => {
        const user = row.original;
        if (!user) return <div>N/A</div>;
        return (
          <div className="min-w-0 max-w-[240px]">
            <div className="flex items-center gap-1.5">
              <Mail className="w-3.5 h-3.5 shrink-0 text-gray-400 dark:text-slate-500" />
              <CopyField value={user.business_email} mono={false} />
            </div>
            <div className="mt-1 flex items-center gap-2">
              {user.email_verify ? (
                <span className="inline-flex items-center gap-1 text-[11px] text-green-700 dark:text-green-400">
                  <ShieldCheck className="w-3 h-3" />verified
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 text-[11px] text-amber-700 dark:text-amber-400">
                  <ShieldAlert className="w-3 h-3" />unverified
                </span>
              )}
              {user.auth_provider && user.auth_provider !== 'email' && (
                <span className="text-[11px] text-gray-500 dark:text-slate-400">via {user.auth_provider}</span>
              )}
            </div>
          </div>
        );
      },
    }),
    columnHelper.accessor('user_status', {
      header: 'Status',
      cell: ({ row }) => {
        const user = row.original;
        if (!user) return <div>N/A</div>;
        
        const isEditing = editingUser?.id === user.id;
        
        return isEditing ? (
          <select
            value={editingUser.user_status || 'active'}
            onChange={(e) => setEditingUser({...editingUser, user_status: e.target.value})}
            className="px-2 py-1 border rounded text-xs"
          >
            {statusOptions.map(status => (
              <option key={status} value={status}>{status}</option>
            ))}
          </select>
        ) : (
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
            user.user_status === 'active'
              ? 'text-green-800 bg-green-100'
              : user.user_status === 'suspended'
              ? 'text-orange-800 bg-orange-100'
              : 'text-red-800 bg-red-100'
          }`}>
            {user.user_status || 'unknown'}
          </span>
        );
      },
    }),
    columnHelper.accessor('plan', {
      header: 'Plan',
      cell: ({ row }) => {
        const user = row.original;
        if (!user) return <div>N/A</div>;
        
        const isEditing = editingUser?.id === user.id;
        
        return isEditing ? (
          <select
            value={editingUser.plan || 'Free'}
            onChange={(e) => setEditingUser({...editingUser, plan: e.target.value})}
            className="px-2 py-1 border rounded text-xs"
          >
            {planOptions.map(plan => (
              <option key={plan} value={plan}>{plan}</option>
            ))}
          </select>
        ) : (
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
            user.plan === 'Enterprise' ? 'text-purple-800 bg-purple-100' :
            user.plan === 'Pro' ? 'text-blue-800 bg-blue-100' :
            user.plan === 'Growth' ? 'text-green-800 bg-green-100' :
            'text-gray-800 bg-gray-100'
          }`}>
            {user.plan || 'Free'}
          </span>
        );
      },
    }),
    columnHelper.accessor('stripe_subscription_id', {
      id: 'subscription',
      header: 'Subscription',
      cell: ({ row }) => {
        const user = row.original;
        if (!user) return <div>N/A</div>;

        const status = (user.subscription_status || '').toLowerCase();
        const statusTone =
          status === 'active' || status === 'trialing' ? 'text-green-800 bg-green-100 dark:text-green-300 dark:bg-green-900/40'
          : status === 'past_due' || status === 'unpaid' ? 'text-red-800 bg-red-100 dark:text-red-300 dark:bg-red-900/40'
          : status === 'canceled' ? 'text-gray-700 bg-gray-200 dark:text-slate-300 dark:bg-slate-700'
          : 'text-gray-600 bg-gray-100 dark:text-slate-400 dark:bg-slate-800';

        const renews = user.subscription_end_date || user.plan_expiry;

        return (
          <div className="min-w-0 max-w-[230px] space-y-1">
            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${statusTone}`}>
              {user.subscription_status || 'no subscription'}
            </span>
            {user.stripe_subscription_id ? (
              <div className="flex items-center gap-1.5">
                <CreditCard className="w-3 h-3 shrink-0 text-gray-400 dark:text-slate-500" />
                <CopyField value={user.stripe_subscription_id} />
              </div>
            ) : (
              <div className="text-xs text-gray-400 dark:text-slate-500">no Stripe id</div>
            )}
            <div className="text-[11px] text-gray-500 dark:text-slate-400">
              {renews ? `renews ${fmtDate(renews)}` : 'no renewal date'}
            </div>
          </div>
        );
      },
    }),
    columnHelper.accessor('total_brand', {
      id: 'usage',
      header: 'Usage Progress',
      cell: ({ row }) => {
        const user = row.original;
        if (!user) return <div>N/A</div>;
        
        // A per-account limit column, when set, is the real limit — the plan
        // table below is only the fallback for accounts with no override.
        const limits = effectiveLimits(user);

        return (
          <div className="space-y-1">
            <ProgressBar
              label="Brand"
              current={user.total_brand || 0}
              limit={limits.brand}
              userId={user.id}
              field="total_brand"
            />
            <ProgressBar
              label="Comp"
              current={user.total_comp || 0}
              limit={limits.comp}
              userId={user.id}
              field="total_comp"
            />
            <ProgressBar
              label="Gap"
              current={user.total_gap || 0}
              limit={limits.gap}
              userId={user.id}
              field="total_gap"
            />
            <ProgressBar
              label="Contact"
              current={user.contact_finder_count || 0}
              limit={limits.contact}
              userId={user.id}
              field="contact_finder_count"
            />
          </div>
        );
      },
    }),
    // Live affiliate check for the whole account in one press. The brands go
    // into a shared queue that the desktops drain; this button never waits on
    // one, so it comes back immediately however many brands it queued.
    columnHelper.accessor('id', {
      id: 'liveCheck',
      header: 'Live check',
      enableSorting: false,
      cell: ({ row }) => {
        const user = row.original;
        if (!user) return <div>N/A</div>;

        const entry = queueByUser.get(user.id);
        const running = entry?.running || 0;
        const queued = entry?.queued || 0;
        const inFlight = running + queued;
        const busy = queueBusy === user.id;
        const batch = entry?.batch;

        return (
          <div className="min-w-[190px]">
            <button
              onClick={() => runUserCheck(user)}
              disabled={busy}
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium disabled:opacity-50 ${
                inFlight
                  ? 'bg-red-50 text-red-700 hover:bg-red-100 dark:bg-red-950 dark:text-red-300'
                  : 'bg-green-50 text-green-700 hover:bg-green-100 dark:bg-green-950 dark:text-green-300'
              }`}
              title={
                inFlight
                  ? `Stop:\n${entry.jobs.map((j) => `${j.brandUrl} — ${j.status}${j.status === 'running' ? ` ${j.percent}%` : ''}`).join('\n')}`
                  : 'Queue every brand this customer holds'
              }
            >
              {busy
                ? <Loader className="w-3 h-3 animate-spin" />
                : inFlight ? <Square className="w-3 h-3" /> : <Play className="w-3 h-3" />}
              {inFlight ? 'Stop' : 'Run'}
            </button>

            {inFlight > 0 ? (
              <div className="mt-1.5">
                <div className="bg-gray-200 dark:bg-slate-700 rounded-full h-1.5">
                  <div className="h-1.5 rounded-full bg-blue-500" style={{ width: `${entry.percent}%` }} />
                </div>
                <p className="text-[11px] text-gray-600 dark:text-slate-400 mt-1">
                  {running} running · {queued} queued
                  {batch?.jobs ? ` · ${batch.done}/${batch.jobs} done` : ''}
                </p>
              </div>
            ) : batch ? (
              <p className="text-[11px] text-gray-500 dark:text-slate-400 mt-1.5">
                Last run {fmtDate(batch.finishedAt || batch.startedAt)} ·{' '}
                {batch.jobs} brand{batch.jobs === 1 ? '' : 's'} ·{' '}
                <span className="text-green-600">+{batch.added}</span>{' / '}
                <span className="text-red-600">−{batch.removed}</span>
                {batch.failed ? ` · ${batch.failed} failed` : ''}
              </p>
            ) : (
              <p className="text-[11px] text-gray-400 dark:text-slate-500 mt-1.5">Never run</p>
            )}
          </div>
        );
      },
    }),
    // Demo logins. A real customer's password is a bcrypt hash and can never be
    // shown here; only accounts created from this panel keep a readable copy.
    columnHelper.accessor('is_demo', {
      id: 'credentials',
      header: 'Demo login',
      cell: ({ row }) => {
        const user = row.original;
        if (!user) return <div>N/A</div>;
        if (!user.is_demo) {
          return (
            <span className="text-xs text-gray-400 dark:text-slate-500" title="Passwords are stored as bcrypt hashes and cannot be recovered. Only accounts created from this panel keep a readable copy.">
              —
            </span>
          );
        }

        const entry = revealed[user.id];
        const busy = revealBusy === user.id;

        return (
          <div className="min-w-[200px] space-y-1.5">
            <button
              onClick={() => revealPassword(user.id)}
              disabled={busy}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium bg-amber-50 text-amber-800 hover:bg-amber-100 dark:bg-amber-950 dark:text-amber-300 disabled:opacity-50"
            >
              {busy
                ? <Loader className="w-3 h-3 animate-spin" />
                : entry ? <EyeOff className="w-3 h-3" /> : <KeyRound className="w-3 h-3" />}
              {entry ? 'Hide' : 'Show password'}
            </button>

            {entry?.error && (
              <p className="text-[11px] text-red-600 dark:text-red-400 whitespace-normal">{entry.error}</p>
            )}

            {entry?.password && (
              <div className="space-y-1">
                <SecretValue value={entry.password} />
                <button
                  onClick={() => navigator.clipboard?.writeText(loginBlock(entry.businessEmail, entry.password))}
                  className="block text-[11px] text-blue-600 dark:text-blue-400 hover:underline"
                >
                  Copy email + password
                </button>
              </div>
            )}
          </div>
        );
      },
    }),
    columnHelper.accessor('id', {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => {
        const user = row.original;
        if (!user) return <div>N/A</div>;
        
        const isEditing = editingUser?.id === user.id;
        
        return isEditing ? (
          <div className="flex items-center gap-2">
            <button
              onClick={saveUser}
              disabled={updating}
              className="text-green-600 hover:text-green-900 p-1 hover:bg-green-50 rounded disabled:opacity-50"
              title="Save Changes"
            >
              {updating ? <Loader className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            </button>
            <button
              onClick={cancelEditing}
              className="text-gray-600 hover:text-gray-900 p-1 hover:bg-gray-50 rounded"
              title="Cancel"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <button
              onClick={() => openDetail(user)}
              className="text-gray-600 dark:text-slate-300 hover:text-gray-900 p-1 hover:bg-gray-100 dark:hover:bg-slate-800 rounded"
              title="View full customer record"
            >
              <Eye className="w-4 h-4" />
            </button>
            <button
              onClick={() => startEditing(user)}
              className="text-blue-600 hover:text-blue-900 p-1 hover:bg-blue-50 dark:hover:bg-slate-800 rounded"
              title="Edit User"
            >
              <Edit className="w-4 h-4" />
            </button>
            <button
              onClick={() => impersonateUser(user.id, `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.business_email)}
              className="text-purple-600 hover:text-purple-900 p-1 hover:bg-purple-50 dark:hover:bg-slate-800 rounded"
              title="Login as this user"
            >
              <LogIn className="w-4 h-4" />
            </button>
          </div>
        );
      },
    }),
    // queueByUser/queueBusy belong here: the Live check cell closes over both,
    // and a column set memoized without them would render the first poll's
    // progress forever.
  ], [editingUser, updating, queueByUser, queueBusy, revealed, revealBusy]);

  // Filter data based on status and plan filters
  const filteredData = useMemo(() => {
    if (!Array.isArray(users)) {
      return [];
    }

    let filtered = users.filter(user => 
      user && 
      typeof user === 'object' && 
      user.id !== null && 
      user.id !== undefined
    );

    if (statusFilter !== 'all') {
      filtered = filtered.filter(user => user.user_status === statusFilter);
    }

    if (planFilter !== 'all') {
      filtered = filtered.filter(user => user.plan === planFilter);
    }

    if (demoFilter !== 'all') {
      filtered = filtered.filter((user) => (demoFilter === 'demo' ? user.is_demo : !user.is_demo));
    }

    if (subFilter !== 'all') {
      filtered = filtered.filter(user => {
        const s = (user.subscription_status || '').toLowerCase();
        if (subFilter === 'paying') return s === 'active' || s === 'trialing';
        if (subFilter === 'past_due') return s === 'past_due' || s === 'unpaid';
        if (subFilter === 'canceled') return s === 'canceled';
        if (subFilter === 'none') return !s;
        return true;
      });
    }

    return filtered;
  }, [users, statusFilter, planFilter, subFilter, demoFilter]);

  const table = useReactTable({
    data: filteredData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    state: {
      globalFilter,
    },
    onGlobalFilterChange: setGlobalFilter,
    // Search the fields an admin actually types, including ones with no column
    // of their own (company) and ones pasted from Stripe.
    globalFilterFn: (row, _columnId, value) => {
      const q = String(value || '').trim().toLowerCase();
      if (!q) return true;
      const u = row.original || {};
      return [
        u.first_name, u.last_name, u.business_email, u.company_name,
        u.plan, u.user_status, u.subscription_status, u.country,
        u.stripe_customer_id, u.stripe_subscription_id, u.id
      ].some((f) => String(f ?? '').toLowerCase().includes(q));
    },
    initialState: {
      pagination: {
        pageSize: 10,
      },
    },
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen dark:bg-slate-950">
        <div className="text-center">
          <Loader className="animate-spin h-12 w-12 border-b-2 border-blue-600 mx-auto" />
          <p className="mt-4 text-gray-600 dark:text-slate-400">Loading users data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen dark:bg-slate-950">
        <div className="text-center">
          <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 dark:text-slate-100 mb-2">Error Loading Users</h2>
          <p className="text-gray-600 dark:text-slate-400 mb-4">{error}</p>
          <button
            onClick={fetchUsersData}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-950 p-6">
      <div className="max-w-[1600px] mx-auto">
        {/* Header */}
        <div className="mb-8 flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-slate-100 flex items-center gap-3">
              <Users className="w-8 h-8 text-blue-600" />
              User Panel
            </h1>
            <p className="text-gray-600 dark:text-slate-400 mt-2">Manage user accounts, plans, and usage limits</p>
          </div>
          <button
            onClick={() => { setShowCreate(true); setCreateError(null); }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 shadow-sm"
          >
            <UserPlus className="w-4 h-4" />
            Create account
          </button>
        </div>

        {/* The one moment the password is on screen for a brand-new account.
            It stays until dismissed because this is what gets pasted to the
            customer — for a demo it can be read again later from its row, for a
            standard account this is the only time it is ever visible. */}
        {created && (
          <div className="rounded-xl border border-green-200 dark:border-green-900 bg-green-50 dark:bg-green-950/40 p-5 mb-8">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <h2 className="flex items-center gap-2 text-sm font-semibold text-green-900 dark:text-green-300">
                  <CheckCircle className="w-4 h-4" />
                  Account ready — {created.user?.plan} plan, active and verified
                </h2>
                <p className="text-xs text-green-800/80 dark:text-green-400/80 mt-1">
                  Nothing was emailed. Send these details to the customer and they can sign in straight away.
                </p>

                <dl className="mt-3 flex flex-wrap items-center gap-x-6 gap-y-2">
                  <div>
                    <dt className="text-[11px] uppercase tracking-wide text-green-800/70 dark:text-green-400/70">Email</dt>
                    <dd className="mt-0.5"><SecretValue value={created.credentials?.businessEmail} label="email" /></dd>
                  </div>
                  <div>
                    <dt className="text-[11px] uppercase tracking-wide text-green-800/70 dark:text-green-400/70">Password</dt>
                    <dd className="mt-0.5"><SecretValue value={created.credentials?.password} /></dd>
                  </div>
                </dl>

                <div className="mt-3 flex flex-wrap items-center gap-3">
                  <button
                    onClick={() => navigator.clipboard?.writeText(
                      loginBlock(created.credentials?.businessEmail, created.credentials?.password)
                    )}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-green-600 text-white text-xs font-medium hover:bg-green-700"
                  >
                    <Copy className="w-3.5 h-3.5" /> Copy email + password
                  </button>
                  <span className="text-[11px] text-green-800/80 dark:text-green-400/80">
                    {created.storedForLaterReveal
                      ? 'Marked as a demo, so this password can be read again from the Demo login column.'
                      : 'Not a demo account — this password is not stored anywhere and cannot be shown again.'}
                  </span>
                </div>
              </div>
              <button
                onClick={() => setCreated(null)}
                className="text-green-700 hover:text-green-900 dark:text-green-400 shrink-0"
                title="Dismiss"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-6 mb-8">
          <div className="bg-white dark:bg-slate-900 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-slate-700">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-slate-400">Total Users</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-slate-100">{users.length}</p>
              </div>
              <Users className="w-8 h-8 text-blue-600" />
            </div>
          </div>

          <div className="bg-white dark:bg-slate-900 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-slate-700">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-slate-400">Active Users</p>
                <p className="text-2xl font-bold text-green-600">
                  {users.filter(u => u.user_status === 'active').length}
                </p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
          </div>

          <div className="bg-white dark:bg-slate-900 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-slate-700">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-slate-400">Inactive Users</p>
                <p className="text-2xl font-bold text-red-600">
                  {users.filter(u => u.user_status === 'inactive').length}
                </p>
              </div>
              <XCircle className="w-8 h-8 text-red-600" />
            </div>
          </div>

          <div className="bg-white dark:bg-slate-900 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-slate-700">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-slate-400">Enterprise Users</p>
                <p className="text-2xl font-bold text-purple-600">
                  {users.filter(u => u.plan === 'Enterprise').length}
                </p>
              </div>
              <AlertCircle className="w-8 h-8 text-purple-600" />
            </div>
          </div>

          <div className="bg-white dark:bg-slate-900 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-slate-700">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-slate-400">Paying</p>
                <p className="text-2xl font-bold text-emerald-600">
                  {users.filter(u => ['active', 'trialing'].includes((u.subscription_status || '').toLowerCase())).length}
                </p>
                <p className="text-[11px] text-gray-500 dark:text-slate-400 mt-0.5">live Stripe subscriptions</p>
              </div>
              <CreditCard className="w-8 h-8 text-emerald-600" />
            </div>
          </div>
        </div>

        {/* Desktop scripts — the switchboard.
            Seven scripts on four desktops behind one `npm start`, where the
            only control anybody had was Ctrl+C on the machine itself, which
            stops all seven. These switches turn one off and leave the rest
            alone. Nothing here reaches into a desktop: each runner asks once a
            minute what it should be running and closes the gap itself, so a
            click takes up to a minute — which is what the notice says. */}
        {/* Shown whenever an admin can see this page, even with no desktop
            reporting. Hiding it until one checks in meant the switches were
            missing exactly when somebody went looking for them to find out why
            nothing was running — and a card that is absent explains nothing,
            where an empty one says which half is not working. Switching
            something off before a machine has ever reported is also perfectly
            meaningful: it takes effect the moment that machine appears. */}
        {desktop && (
          <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-gray-200 dark:border-slate-700 mb-6">
            <div className="px-6 py-4 flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 dark:border-slate-700">
              <div className="min-w-0">
                <h2 className="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-slate-100">
                  <Power className={`w-4 h-4 ${desktop.online > 0 ? 'text-green-600' : 'text-gray-400'}`} />
                  System control
                </h2>
                <p className="text-xs text-gray-600 dark:text-slate-400 mt-1">
                  {(() => {
                    // Counted apart because they answer different questions. A
                    // reachable backend with every desktop down is not "1 of 5
                    // online" — it is a fleet that is not running, behind a
                    // panel that is.
                    const boxes = desktop.machines.filter((m) => (m.scope || 'desktop') !== 'server');
                    const server = desktop.machines.find((m) => m.scope === 'server');
                    const up = boxes.filter((m) => m.online).length;
                    const desktops = boxes.length === 0
                      ? 'No desktop has checked in yet'
                      : `${up} of ${boxes.length} desktop${boxes.length === 1 ? '' : 's'} checked in`;
                    const backend = server
                      ? (server.online ? 'backend reporting' : 'backend NOT reporting')
                      : 'backend has not reported yet';
                    return `${desktops} · ${backend}`;
                  })()}
                </p>
              </div>
              {desktopNotice && (
                <p className={`text-xs ${desktopNotice.ok ? 'text-green-700 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                  {desktopNotice.text}
                </p>
              )}
            </div>

            {/* One card per system. Which half of the switchboard a card
                belongs to is said in its own subtitle rather than by a heading
                over a group, because the delay between clicking and something
                happening is a property of the machine, not of a category: a
                desktop script is a process the runner has to be told about on
                its next poll, and a server job is a schedule that is already
                ticking and simply starts returning early.

                A machine that has stopped checking in gets a dashed card that
                says so, in place of the list this used to keep at the bottom.
                It is the one thing these switches cannot do anything about and
                the one most worth knowing — it is not running what the panel
                says it is running — and it belongs next to that machine's
                switches, not in a footnote away from them. */}
            <div className="p-6 grid grid-cols-1 lg:grid-cols-2 gap-4">
              {systems.map((system) => (
                <SystemCard
                  key={system.id}
                  system={system}
                  saving={desktopSaving}
                  onToggle={toggleModule}
                />
              ))}
            </div>
          </div>
        )}

        {/* Which Claude model each affiliate agent runs on, for every account. */}
        <AgentModelsCard />

        {/* Desktop fleet — live affiliate check.
            The machines run mars-live-affiliate-checker and poll for work
            themselves; there is no terminal to watch once the sweep is spread
            over four of them, so this is it. */}
        {(queue.workers.length > 0 || fleetBusy) && (
          <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-gray-200 dark:border-slate-700 mb-6">
            <div className="px-6 py-4 flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 dark:border-slate-700">
              <h2 className="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-slate-100">
                <Radar className={`w-4 h-4 text-blue-600 ${fleetBusy ? 'animate-pulse' : ''}`} />
                Live check fleet
              </h2>
              <p className="text-xs text-gray-600 dark:text-slate-400">
                {queue.fleet?.busy || 0} of {queue.fleet?.online || 0} desktop{(queue.fleet?.online || 0) === 1 ? '' : 's'} working
                {queue.fleet?.queued ? ` · ${queue.fleet.queued} brand${queue.fleet.queued === 1 ? '' : 's'} waiting` : ''}
                {queue.fleet?.waitingCustomers ? ` · ${queue.fleet.waitingCustomers} customer${queue.fleet.waitingCustomers === 1 ? '' : 's'} in line` : ''}
              </p>
            </div>

            <div className="p-6 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {fleetMachines.map((box) => (
                <div
                  key={box.id}
                  className={`rounded-lg border p-3 ${
                    box.online
                      ? 'border-gray-200 dark:border-slate-700'
                      : 'border-dashed border-gray-300 dark:border-slate-700 opacity-60'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <Monitor className={`w-4 h-4 ${box.online ? 'text-green-600' : 'text-gray-400'}`} />
                    <span className="text-sm font-medium text-gray-900 dark:text-slate-100 truncate">
                      {box.hostname}
                    </span>
                    <span className="text-[11px] text-gray-500 dark:text-slate-400 shrink-0">
                      {box.workers.length} checker{box.workers.length === 1 ? '' : 's'}
                    </span>
                  </div>

                  {/* Which queues this machine polls — one chip per checker it
                      is running. A worker that reports none takes both, which is
                      not a setting: it is a checkout from before the fleets were
                      split, and it is how a monitor job turns into a 44k-row
                      sweep. */}
                  <div className="mt-1.5 flex flex-wrap gap-1">
                    {box.workers.map((worker) => (
                      workerFleet(worker) ? (
                        <span
                          key={worker.workerId}
                          title={worker.workerId}
                          className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-medium ${
                            worker.online ? '' : 'opacity-60'
                          } ${
                            worker.scopes?.includes('live')
                              ? 'bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300'
                              : 'bg-blue-100 text-blue-800 dark:bg-blue-950/60 dark:text-blue-300'
                          }`}
                        >
                          {workerFleet(worker)}
                        </span>
                      ) : worker.online ? (
                        <span
                          key={worker.workerId}
                          className="inline-block px-1.5 py-0.5 rounded text-[10px] font-medium bg-red-100 text-red-800 dark:bg-red-950/60 dark:text-red-300"
                          title="This checker has not said which queue it polls, so it will claim anything — including a monitor job it would then run against the whole affiliate table. Run git pull in its checkout."
                        >
                          Queue not reported
                        </span>
                      ) : null
                    ))}
                  </div>

                  {/* One block per job actually in flight. A machine running
                      three checkers can be working three brands at once, and
                      collapsing that into a single bar would hide two of them. */}
                  {box.busy.length > 0 ? (
                    box.busy.map((worker) => (
                      <div key={worker.workerId} className="mt-2">
                        <p className="text-xs text-gray-900 dark:text-slate-200 truncate" title={worker.brandName || worker.brandUrl}>
                          {worker.brandUrl}
                        </p>
                        <div className="mt-1 bg-gray-200 dark:bg-slate-700 rounded-full h-1.5">
                          <div
                            className={`h-1.5 rounded-full ${worker.online ? 'bg-blue-500' : 'bg-gray-400 dark:bg-slate-600'}`}
                            style={{ width: `${worker.percent}%` }}
                          />
                        </div>
                        {/* job_id on the worker row outlives the worker: a
                            desktop unplugged mid-run leaves its last job
                            attached here forever. Drawing that as a live
                            progress bar shows a dead machine working, which is
                            how a stalled run hides — the header counts it out
                            of `busy` and the card does not, so the two disagree
                            and the card is the one people read. The next claim
                            from any worker re-queues it. */}
                        <p className="mt-1 text-[11px] text-gray-500 dark:text-slate-400 truncate">
                          {worker.online
                            ? `${worker.percent}% · ${worker.ownerEmail || 'no customer'}`
                            : `Stopped at ${worker.percent}% — went offline ${fmtDate(worker.lastSeen)}, job goes back in the queue`}
                        </p>
                      </div>
                    ))
                  ) : (
                    <p className="mt-2 text-xs text-gray-500 dark:text-slate-400">
                      {box.online ? 'Idle — waiting for work' : `Offline since ${fmtDate(box.lastSeen)}`}
                    </p>
                  )}
                </div>
              ))}
            </div>

            {waitingCustomers.length > 0 && (
              <div className="px-6 pb-5 -mt-2">
                <p className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-slate-400 mb-2">
                  Next in line — a freed desktop takes whoever has the fewest brands running
                </p>
                <div className="flex flex-wrap gap-2">
                  {waitingCustomers.map((entry, index) => (
                    <span
                      key={entry.userId ?? 'adhoc'}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gray-100 dark:bg-slate-800 text-xs text-gray-700 dark:text-slate-300"
                    >
                      <span className="text-gray-400">{index + 1}.</span>
                      {entry.name || entry.email || `user ${entry.userId}`}
                      <span className="text-gray-500 dark:text-slate-400">
                        {entry.queued} waiting{entry.running ? ` · ${entry.running} running` : ''}
                      </span>
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Lost affiliate monitoring — every account at once.
            The per-brand toggle and Check now button live on the customer's own
            brand card; this is the operator's view of all of them together, and
            the one button that queues the weekly pass by hand rather than
            waiting for Monday. */}
        <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-gray-200 dark:border-slate-700 mb-6">
          <div className="px-6 py-4 flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 dark:border-slate-700">
            <div className="min-w-0">
              <h2 className="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-slate-100">
                <ShieldAlert className={`w-4 h-4 text-amber-600 ${monitorBusyNow ? 'animate-pulse' : ''}`} />
                Lost affiliate monitoring
              </h2>
              <p className="text-xs text-gray-600 dark:text-slate-400 mt-1">
                {monitor
                  ? <>
                      {monitor.totals.brands} brand{monitor.totals.brands === 1 ? '' : 's'} monitored
                      {' · '}{monitor.totals.accounts} account{monitor.totals.accounts === 1 ? '' : 's'}
                      {' · '}{monitor.totals.openLost} open loss{monitor.totals.openLost === 1 ? '' : 'es'}
                      {monitor.totals.inFlight ? ` · ${monitor.totals.inFlight} checking now` : ''}
                    </>
                  : 'Loading…'}
              </p>
            </div>

            <div className="flex items-center gap-2">
              {monitor?.totals?.brands > 0 && (
                <button
                  onClick={() => setMonitorOpen((v) => !v)}
                  className="px-3 py-2 text-xs font-medium rounded-lg border border-gray-200 dark:border-slate-700 text-gray-700 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-800 inline-flex items-center gap-1"
                >
                  <ChevronRight className={`w-3.5 h-3.5 transition-transform ${monitorOpen ? 'rotate-90' : ''}`} />
                  {monitorOpen ? 'Hide brands' : 'Show brands'}
                </button>
              )}
              <button
                onClick={runMonitorAll}
                disabled={monitorBusy}
                className="px-4 py-2 text-xs font-medium rounded-lg bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center gap-2"
                title="Queue a lost-affiliate check for every monitored brand on every account"
              >
                {monitorBusy
                  ? <><Loader className="w-3.5 h-3.5 animate-spin" /> Queueing…</>
                  : <><Play className="w-3.5 h-3.5" /> Check every account</>}
              </button>
            </div>
          </div>

          {/* Whether anything will actually pick the work up. This is the whole
              reason the card exists rather than a bare button: with no worker on
              the monitor queue the jobs sit at "queued" indefinitely, which
              reads as a slow fleet and is really a machine nobody started. */}
          <div className="px-6 py-3 border-b border-gray-100 dark:border-slate-800 flex flex-wrap items-center gap-x-4 gap-y-2">
            {monitor?.fleet?.liveDeclared > 0 ? (
              <span className="inline-flex items-center gap-1.5 text-xs text-green-700 dark:text-green-400">
                <CheckCircle className="w-3.5 h-3.5" />
                {monitor.fleet.liveDeclared} desktop{monitor.fleet.liveDeclared === 1 ? '' : 's'} on the monitor queue
                <span className="text-gray-500 dark:text-slate-500">
                  ({monitor.fleet.live
                    .filter((w) => w.scopes?.includes('live'))
                    .map((w) => w.hostname || w.workerId).join(', ')})
                </span>
              </span>
            ) : monitor?.fleet?.liveOnline > 0 ? (
              /* Something will claim the work, but only because it claims
                 everything. That is worse than an empty queue, not better: the
                 job starts, looks healthy, and reads the whole affiliate table
                 for a brand with a few dozen affiliates. Green here would be a
                 lie of exactly the kind this row exists to prevent. */
              <span className="inline-flex items-center gap-1.5 text-xs text-amber-700 dark:text-amber-400">
                <AlertCircle className="w-3.5 h-3.5" />
                No desktop has declared the monitor queue. Work will be picked up by a checkout
                that predates the split and will scan the whole affiliate table for each brand.
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5 text-xs text-red-700 dark:text-red-400">
                <AlertCircle className="w-3.5 h-3.5" />
                No desktop is running the lost-affiliate checker — anything queued will wait.
                Start <code className="px-1 rounded bg-gray-100 dark:bg-slate-800">mars-weekly-lost-affiliate</code> on a machine.
              </span>
            )}

            {monitor?.fleet?.unscoped?.length > 0 && (
              <span
                className="inline-flex items-center gap-1.5 text-xs text-amber-700 dark:text-amber-400"
                title="A worker that does not report its queue claims from both, and runs a monitor job against the whole affiliate table instead of the brand's own affiliates — minutes of work becoming an afternoon."
              >
                <AlertCircle className="w-3.5 h-3.5" />
                {monitor.fleet.unscoped.join(', ')} {monitor.fleet.unscoped.length === 1 ? 'is' : 'are'} not
                reporting a queue — <code className="px-1 rounded bg-gray-100 dark:bg-slate-800">git pull</code> that checkout
              </span>
            )}
          </div>

          {monitorOpen && monitor?.brands?.length > 0 && (
            <div className="px-6 py-4 overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left text-gray-500 dark:text-slate-400">
                    <th className="pb-2 pr-4 font-medium">Brand</th>
                    <th className="pb-2 pr-4 font-medium">Account</th>
                    <th className="pb-2 pr-4 font-medium text-right">Open losses</th>
                    <th className="pb-2 pr-4 font-medium">Last check</th>
                  </tr>
                </thead>
                <tbody>
                  {monitor.brands.map((b) => (
                    <tr key={b.brandId} className="border-t border-gray-100 dark:border-slate-800">
                      <td className="py-2 pr-4 text-gray-900 dark:text-slate-200 whitespace-nowrap">
                        {b.brandUrl}
                      </td>
                      <td className="py-2 pr-4 text-gray-600 dark:text-slate-400 truncate max-w-[220px]" title={b.email || ''}>
                        {b.account}
                      </td>
                      <td className={`py-2 pr-4 text-right font-medium ${
                        b.openLost > 0 ? 'text-red-600 dark:text-red-400' : 'text-gray-400 dark:text-slate-600'
                      }`}>
                        {b.openLost || '—'}
                      </td>
                      <td className="py-2 pr-4 text-gray-600 dark:text-slate-400 whitespace-nowrap">
                        {!b.job ? (
                          <span className="text-gray-400 dark:text-slate-600">never checked</span>
                        ) : b.job.status === 'running' ? (
                          <span className="text-blue-600 dark:text-blue-400">
                            checking · {b.job.processed}/{b.job.total}
                            {!b.job.workerAlive && ' · worker not responding'}
                          </span>
                        ) : b.job.status === 'queued' ? (
                          <span className="text-amber-600 dark:text-amber-400">queued</span>
                        ) : (
                          <>
                            {fmtDateTime(b.job.finishedAt)}
                            <span className="text-gray-400 dark:text-slate-600">
                              {' · '}{b.job.status}
                              {b.job.status === 'completed' ? ` · ${b.job.removed} lost` : ''}
                            </span>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Result of the last Check-every-account press. */}
        {monitorNotice && (
          <div className={`rounded-xl border p-4 mb-6 ${
            monitorNotice.ok
              ? 'border-green-200 bg-green-50 dark:bg-green-950/40 dark:border-green-900'
              : 'border-red-200 bg-red-50 dark:bg-red-950/40 dark:border-red-900'
          }`}>
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <p className={`text-sm font-medium ${monitorNotice.ok ? 'text-green-800 dark:text-green-300' : 'text-red-800 dark:text-red-300'}`}>
                  {monitorNotice.ok
                    ? `${monitorNotice.queued} brand${monitorNotice.queued === 1 ? '' : 's'} queued` +
                      (monitorNotice.skipped ? `, ${monitorNotice.skipped} skipped` : '')
                    : (monitorNotice.message || 'Failed')}
                </p>
                {monitorNotice.detail?.queued?.length > 0 && (
                  <p className="text-xs text-gray-700 dark:text-slate-300 mt-1">
                    Queued: {monitorNotice.detail.queued.map((b) => b.brandUrl).join(', ')}
                  </p>
                )}
                {monitorNotice.detail?.alreadyActive?.length > 0 && (
                  <p className="text-xs text-gray-600 dark:text-slate-400 mt-1">
                    Already being checked: {monitorNotice.detail.alreadyActive.map((b) => b.brandUrl).join(', ')}
                  </p>
                )}
                {monitorNotice.detail?.failed?.length > 0 && (
                  <p className="text-xs text-red-700 dark:text-red-400 mt-1">
                    Could not queue: {monitorNotice.detail.failed.map((b) => `${b.brandUrl} (${b.reason})`).join(', ')}
                  </p>
                )}
                {monitorNotice.ok && !monitorNotice.fleet?.liveDeclared && monitorNotice.queued > 0 && (
                  <p className="text-xs text-red-700 dark:text-red-400 mt-1">
                    {monitorNotice.fleet?.liveOnline
                      ? 'No desktop has declared the monitor queue — these will be picked up by a checkout ' +
                        'that scans the whole affiliate table per brand. Expect hours, not minutes.'
                      : 'Nothing is running the lost-affiliate checker, so these are waiting rather than starting.'}
                  </p>
                )}
              </div>
              <button
                onClick={() => setMonitorNotice(null)}
                className="text-gray-500 hover:text-gray-800 dark:hover:text-slate-200 shrink-0"
                title="Dismiss"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {/* Result of the last Run/Stop press. Structured enough that an alert()
            would lose it — brands can be queued, already in flight, skipped
            mid-pipeline, or left out over the per-run limit, all at once. */}
        {queueNotice && (
          <div className={`rounded-xl border p-4 mb-6 ${
            queueNotice.ok
              ? 'border-green-200 bg-green-50 dark:bg-green-950/40 dark:border-green-900'
              : 'border-red-200 bg-red-50 dark:bg-red-950/40 dark:border-red-900'
          }`}>
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <p className={`text-sm font-medium ${queueNotice.ok ? 'text-green-800 dark:text-green-300' : 'text-red-800 dark:text-red-300'}`}>
                  {queueNotice.name}: {queueNotice.message || (queueNotice.ok ? 'Done' : 'Failed')}
                </p>
                {queueNotice.queued?.length > 0 && (
                  <p className="text-xs text-gray-700 dark:text-slate-300 mt-1">
                    Queued: {queueNotice.queued.map((b) => b.brandUrl).join(', ')}
                  </p>
                )}
                {queueNotice.alreadyActive?.length > 0 && (
                  <p className="text-xs text-gray-600 dark:text-slate-400 mt-1">
                    Already in flight: {queueNotice.alreadyActive.map((b) => b.brandUrl).join(', ')}
                  </p>
                )}
                {queueNotice.skipped?.length > 0 && (
                  <p className="text-xs text-gray-600 dark:text-slate-400 mt-1">
                    Skipped: {queueNotice.skipped.map((b) => `${b.brandUrl} (${b.reason})`).join(', ')}
                  </p>
                )}
              </div>
              <button
                onClick={() => setQueueNotice(null)}
                className="text-gray-500 hover:text-gray-800 dark:hover:text-slate-200 shrink-0"
                title="Dismiss"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {/* Filters and Search */}
        <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-gray-200 dark:border-slate-700 mb-6">
          <div className="p-6 border-b border-gray-200 dark:border-slate-700">
            <div className="flex flex-col sm:flex-row gap-4">
              {/* Global Search */}
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-slate-500" />
                  <input
                    type="text"
                    placeholder="Search users..."
                    value={globalFilter ?? ''}
                    onChange={(e) => setGlobalFilter(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>

              {/* Status Filter */}
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-gray-500 dark:text-slate-400" />
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-3 py-2 border border-gray-300 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="all">All Status</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="suspended">Suspended</option>
                </select>
              </div>

              {/* Plan Filter */}
              <div className="flex items-center gap-2">
                <select
                  value={planFilter}
                  onChange={(e) => setPlanFilter(e.target.value)}
                  className="px-3 py-2 border border-gray-300 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="all">All Plans</option>
                  {planOptions.map(plan => (
                    <option key={plan} value={plan}>{plan}</option>
                  ))}
                </select>
              </div>

              {/* Demo Filter */}
              <div className="flex items-center gap-2">
                <select
                  value={demoFilter}
                  onChange={(e) => setDemoFilter(e.target.value)}
                  className="px-3 py-2 border border-gray-300 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="all">All accounts</option>
                  <option value="demo">Demo accounts</option>
                  <option value="real">Real accounts</option>
                </select>
              </div>

              {/* Subscription Filter */}
              <div className="flex items-center gap-2">
                <select
                  value={subFilter}
                  onChange={(e) => setSubFilter(e.target.value)}
                  className="px-3 py-2 border border-gray-300 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="all">All Subscriptions</option>
                  <option value="paying">Paying (active/trialing)</option>
                  <option value="past_due">Past due / unpaid</option>
                  <option value="canceled">Canceled</option>
                  <option value="none">No subscription</option>
                </select>
              </div>
            </div>
            <p className="mt-3 text-xs text-gray-500 dark:text-slate-400">
              Search matches name, email, company, plan and Stripe ids. Click the eye icon on a row for the full customer record.
            </p>
          </div>

          {/* TanStack Table */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-slate-800">
                {table.getHeaderGroups().map(headerGroup => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map(header => (
                      <th
                        key={header.id}
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wider cursor-pointer"
                        onClick={header.column.getToggleSortingHandler()}
                      >
                        <div className="flex items-center gap-2">
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {header.column.getCanSort() && (
                            <ArrowUpDown className="w-3 h-3" />
                          )}
                        </div>
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody className="bg-white dark:bg-slate-900 divide-y divide-gray-200 dark:divide-slate-700">
                {table.getRowModel().rows.length === 0 ? (
                  <tr>
                    <td colSpan={columns.length} className="px-6 py-12 text-center text-gray-500 dark:text-slate-400">
                      <Users className="w-12 h-12 mx-auto mb-4 text-gray-300 dark:text-slate-600" />
                      No users found matching your criteria
                    </td>
                  </tr>
                ) : (
                  table.getRowModel().rows.map(row => (
                    <tr key={row.id} className="hover:bg-gray-50 dark:hover:bg-slate-800">
                      {row.getVisibleCells().map(cell => (
                        <td key={cell.id} className="px-6 py-4 whitespace-nowrap">
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="px-6 py-4 border-t border-gray-200 dark:border-slate-700">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-700 dark:text-slate-300">
                  Showing {table.getState().pagination.pageIndex * table.getState().pagination.pageSize + 1} to{' '}
                  {Math.min((table.getState().pagination.pageIndex + 1) * table.getState().pagination.pageSize, table.getFilteredRowModel().rows.length)} of{' '}
                  {table.getFilteredRowModel().rows.length} results
                </span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => table.setPageIndex(0)}
                  disabled={!table.getCanPreviousPage()}
                  className="p-1 rounded hover:bg-gray-100 dark:hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed dark:text-slate-300"
                >
                  <ChevronsLeft className="w-4 h-4" />
                </button>
                <button
                  onClick={() => table.previousPage()}
                  disabled={!table.getCanPreviousPage()}
                  className="p-1 rounded hover:bg-gray-100 dark:hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed dark:text-slate-300"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <span className="text-sm text-gray-700 dark:text-slate-300">
                  Page {table.getState().pagination.pageIndex + 1} of {table.getPageCount()}
                </span>
                <button
                  onClick={() => table.nextPage()}
                  disabled={!table.getCanNextPage()}
                  className="p-1 rounded hover:bg-gray-100 dark:hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed dark:text-slate-300"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
                <button
                  onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                  disabled={!table.getCanNextPage()}
                  className="p-1 rounded hover:bg-gray-100 dark:hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed dark:text-slate-300"
                >
                  <ChevronsRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* --- Create account ---------------------------------------------- */}
      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowCreate(false)} aria-hidden="true" />
          <form
            onSubmit={createAccount}
            className="relative w-full max-w-2xl max-h-full overflow-y-auto bg-white dark:bg-slate-900 rounded-xl shadow-xl"
          >
            <div className="sticky top-0 bg-white dark:bg-slate-900 border-b border-gray-200 dark:border-slate-700 px-6 py-4 flex items-start justify-between gap-4">
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-slate-100 flex items-center gap-2">
                  <UserPlus className="w-5 h-5 text-blue-600" /> Create account
                </h2>
                <p className="text-sm text-gray-500 dark:text-slate-400 mt-0.5">
                  Active and verified the moment it is created — no verification email, no waiting.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowCreate(false)}
                className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-800 text-gray-600 dark:text-slate-300"
                title="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="px-6 py-5 space-y-5">
              {createError && (
                <div className="rounded-lg border border-red-200 dark:border-red-900 bg-red-50 dark:bg-red-950/40 px-4 py-3 text-sm text-red-700 dark:text-red-300">
                  {createError}
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <label className="sm:col-span-2">
                  <span className="text-xs font-medium text-gray-700 dark:text-slate-300">Email *</span>
                  <input
                    type="email"
                    required
                    autoFocus
                    value={createForm.businessEmail}
                    onChange={(e) => setCreateForm({ ...createForm, businessEmail: e.target.value })}
                    placeholder="client@example.com"
                    className="mt-1 w-full px-3 py-2 border border-gray-300 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <span className="text-[11px] text-gray-500 dark:text-slate-400">This is the login. It does not have to be a real inbox.</span>
                </label>

                <label>
                  <span className="text-xs font-medium text-gray-700 dark:text-slate-300">First name</span>
                  <input
                    value={createForm.firstName}
                    onChange={(e) => setCreateForm({ ...createForm, firstName: e.target.value })}
                    className="mt-1 w-full px-3 py-2 border border-gray-300 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </label>
                <label>
                  <span className="text-xs font-medium text-gray-700 dark:text-slate-300">Last name</span>
                  <input
                    value={createForm.lastName}
                    onChange={(e) => setCreateForm({ ...createForm, lastName: e.target.value })}
                    className="mt-1 w-full px-3 py-2 border border-gray-300 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </label>
                <label className="sm:col-span-2">
                  <span className="text-xs font-medium text-gray-700 dark:text-slate-300">Company</span>
                  <input
                    value={createForm.companyName}
                    onChange={(e) => setCreateForm({ ...createForm, companyName: e.target.value })}
                    placeholder="The customer's company"
                    className="mt-1 w-full px-3 py-2 border border-gray-300 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </label>

                <label>
                  <span className="text-xs font-medium text-gray-700 dark:text-slate-300">Plan</span>
                  <select
                    value={createForm.plan}
                    onChange={(e) => setCreateForm({ ...createForm, plan: e.target.value })}
                    className="mt-1 w-full px-3 py-2 border border-gray-300 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    {planOptions.map((plan) => <option key={plan} value={plan}>{plan}</option>)}
                  </select>
                </label>
                <label>
                  <span className="text-xs font-medium text-gray-700 dark:text-slate-300">Password</span>
                  <input
                    value={createForm.password}
                    onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
                    placeholder="Leave blank to generate one"
                    className="mt-1 w-full px-3 py-2 border border-gray-300 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </label>
              </div>

              <div>
                <p className="text-xs font-medium text-gray-700 dark:text-slate-300 mb-2">Limits</p>
                <p className="text-[11px] text-gray-500 dark:text-slate-400 mb-2">
                  A normal sign-up starts at zero of everything. These are the per-account overrides, so the demo has something to actually use.
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                  {[
                    ['brandLimit', 'Brands'],
                    ['compLimit', 'Competitors'],
                    ['gapLimit', 'Gaps'],
                    ['emailLimit', 'Contacts'],
                    ['credits', 'Credits']
                  ].map(([key, label]) => (
                    <label key={key}>
                      <span className="text-[11px] text-gray-600 dark:text-slate-400">{label}</span>
                      <input
                        type="number"
                        min="0"
                        max="1000"
                        value={createForm[key]}
                        onChange={(e) => setCreateForm({ ...createForm, [key]: e.target.value })}
                        className="mt-1 w-full px-2 py-1.5 border border-gray-300 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </label>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <label>
                  <span className="text-xs font-medium text-gray-700 dark:text-slate-300">Demo runs for (days)</span>
                  <input
                    type="number"
                    min="0"
                    max="365"
                    value={createForm.expiresInDays}
                    onChange={(e) => setCreateForm({ ...createForm, expiresInDays: e.target.value })}
                    className="mt-1 w-full px-3 py-2 border border-gray-300 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  {/* Said plainly, because the app does not enforce plan_expiry
                      anywhere — it is shown to the customer, not acted on. */}
                  <span className="text-[11px] text-amber-700 dark:text-amber-400">
                    Shown as the end date on the account. It does not lock the account by itself — set the status to suspended when the demo is over. 0 = no end date.
                  </span>
                </label>

                <label className="flex items-start gap-2 pt-6">
                  <input
                    type="checkbox"
                    checked={createForm.isDemo}
                    onChange={(e) => setCreateForm({ ...createForm, isDemo: e.target.checked })}
                    className="mt-0.5"
                  />
                  <span>
                    <span className="text-xs font-medium text-gray-700 dark:text-slate-300">Demo account — keep the password readable</span>
                    <span className="block text-[11px] text-gray-500 dark:text-slate-400">
                      Stores the password so it can be looked up again from the user list. Untick for a real customer: then the password is hashed only and this screen is the one and only time you will see it.
                    </span>
                  </span>
                </label>
              </div>
            </div>

            <div className="sticky bottom-0 bg-white dark:bg-slate-900 border-t border-gray-200 dark:border-slate-700 px-6 py-4 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowCreate(false)}
                className="px-4 py-2 text-sm rounded-lg text-gray-700 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-800"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={creating || !createForm.businessEmail}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {creating ? <Loader className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
                Create account
              </button>
            </div>
          </form>
        </div>
      )}

      {/* --- Customer detail drawer ------------------------------------- */}
      {detailUser && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={closeDetail}
            aria-hidden="true"
          />
          <div className="relative w-full max-w-2xl h-full bg-white dark:bg-slate-900 shadow-xl overflow-y-auto">
            {/* Header */}
            <div className="sticky top-0 z-10 bg-white dark:bg-slate-900 border-b border-gray-200 dark:border-slate-700 px-6 py-4 flex items-start justify-between gap-4">
              <div className="min-w-0">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-slate-100 truncate">
                  {`${detailUser.first_name || ''} ${detailUser.last_name || ''}`.trim() || detailUser.business_email || `User #${detailUser.id}`}
                </h2>
                <p className="text-sm text-gray-500 dark:text-slate-400 truncate">
                  {detailUser.business_email} · account #{detailUser.id}
                </p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button
                  onClick={() => impersonateUser(detailUser.id, `${detailUser.first_name || ''} ${detailUser.last_name || ''}`.trim() || detailUser.business_email)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg bg-purple-600 text-white hover:bg-purple-700"
                  title="Login as this user"
                >
                  <LogIn className="w-4 h-4" /> Login as
                </button>
                <button
                  onClick={closeDetail}
                  className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-800 text-gray-600 dark:text-slate-300"
                  title="Close"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="px-6 py-5">
              {detailLoading && (
                <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-slate-400 mb-4">
                  <Loader className="w-4 h-4 animate-spin" /> Loading full record…
                </div>
              )}
              {detailError && (
                <div className="mb-4 rounded-lg border border-red-200 dark:border-red-900 bg-red-50 dark:bg-red-950/40 px-4 py-3 text-sm text-red-700 dark:text-red-300">
                  {detailError}
                </div>
              )}

              {/* The list row is shown straight away; the fetched record replaces
                  it field-by-field as soon as it arrives. */}
              {(() => {
                const u = detail?.user || detailUser;
                const limits = effectiveLimits(detailUser);
                return (
                  <>
                    <Section title="Account" icon={Users}>
                      <dl className="grid grid-cols-2 gap-x-4 gap-y-3">
                        <Field label="Email"><CopyField value={u.business_email} mono={false} /></Field>
                        <Field label="Email verified">
                          {u.email_verify
                            ? <span className="text-green-700 dark:text-green-400">Yes{u.email_verified_at ? ` · ${fmtDate(u.email_verified_at)}` : ''}</span>
                            : <span className="text-amber-700 dark:text-amber-400">No</span>}
                        </Field>
                        <Field label="Company">{u.company_name || '—'}</Field>
                        <Field label="Phone">{u.phone_number || '—'}</Field>
                        <Field label="Country">{u.country || '—'}</Field>
                        <Field label="Status">{u.user_status || '—'}</Field>
                        <Field label="Sign-in method">
                          {u.auth_provider || 'email'}{u.has_password === false ? ' (no password set)' : ''}
                        </Field>
                        <Field label="Sender email">{u.sender_email || '—'}</Field>
                        <Field label="Last updated">{fmtDate(u.updated_at)}</Field>
                        <Field label="Password reset pending">{u.has_reset_token_pending ? 'Yes' : 'No'}</Field>
                        <Field label="Last login">
                          {u.last_login_at ? fmtDateTime(u.last_login_at) : 'never signed in'}
                        </Field>
                        <Field label="Last login IP">
                          {u.last_login_ip ? <CopyField value={u.last_login_ip} /> : '—'}
                        </Field>
                      </dl>
                    </Section>

                    {(detailUser.is_demo || u.is_demo) && (
                      <Section title="Demo login" icon={KeyRound}>
                        <p className="text-xs text-gray-600 dark:text-slate-400 mb-3">
                          Created {fmtDate(u.demo_created_at || detailUser.demo_created_at)}
                          {(u.demo_created_by || detailUser.demo_created_by) ? ` by ${u.demo_created_by || detailUser.demo_created_by}` : ''}.
                          The password is stored in readable form for this account only.
                        </p>
                        <div className="flex flex-wrap items-center gap-3">
                          <button
                            onClick={() => revealPassword(detailUser.id)}
                            disabled={revealBusy === detailUser.id}
                            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium bg-amber-50 text-amber-800 hover:bg-amber-100 dark:bg-amber-950 dark:text-amber-300 disabled:opacity-50"
                          >
                            {revealBusy === detailUser.id
                              ? <Loader className="w-3 h-3 animate-spin" />
                              : revealed[detailUser.id] ? <EyeOff className="w-3 h-3" /> : <KeyRound className="w-3 h-3" />}
                            {revealed[detailUser.id] ? 'Hide password' : 'Show password'}
                          </button>
                          {revealed[detailUser.id]?.password && (
                            <>
                              <SecretValue value={revealed[detailUser.id].password} />
                              <button
                                onClick={() => navigator.clipboard?.writeText(
                                  loginBlock(revealed[detailUser.id].businessEmail, revealed[detailUser.id].password)
                                )}
                                className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                              >
                                Copy email + password
                              </button>
                            </>
                          )}
                          {revealed[detailUser.id]?.error && (
                            <span className="text-xs text-red-600 dark:text-red-400">{revealed[detailUser.id].error}</span>
                          )}
                        </div>
                      </Section>
                    )}

                    <Section title="Plan & subscription" icon={CreditCard}>
                      <dl className="grid grid-cols-2 gap-x-4 gap-y-3">
                        <Field label="Plan">{u.plan || 'Free'}</Field>
                        <Field label="Subscription status">{u.subscription_status || 'none'}</Field>
                        <Field label="Stripe customer id"><CopyField value={u.stripe_customer_id} /></Field>
                        <Field label="Stripe subscription id"><CopyField value={u.stripe_subscription_id} /></Field>
                        <Field label="Stripe price id"><CopyField value={u.stripe_price_id} /></Field>
                        <Field label="Credits">{u.credits ?? 0}</Field>
                        <Field label="Plan started">{fmtDate(u.plan_started_date || u.subscription_start_date)}</Field>
                        <Field label="Plan expires">{fmtDate(u.plan_expiry || u.subscription_end_date)}</Field>
                        <Field label="Trial ends">{fmtDate(u.trial_end_date)}</Field>
                        <Field label="Plan changes">{u.plan_update_cnt ?? 0}</Field>
                        <Field label="Billing name">{u.billing_name || '—'}</Field>
                        <Field label="Billing email">{u.billing_email || '—'}</Field>
                        <Field label="Billing address">{u.billing_address || '—'}</Field>
                        <Field label="Tax id">{u.tax_id || '—'}</Field>
                      </dl>
                    </Section>

                    <Section title="Usage vs limits" icon={AlertCircle}>
                      <dl className="grid grid-cols-2 gap-x-4 gap-y-3">
                        <Field label="Brands">{`${u.total_brand ?? 0} / ${limits.brand === 'unlimited' ? '∞' : limits.brand}`}</Field>
                        <Field label="Competitors">{`${u.total_comp ?? 0} / ${limits.comp === 'unlimited' ? '∞' : limits.comp}`}</Field>
                        <Field label="Affiliate gaps">{`${u.total_gap ?? 0} / ${limits.gap === 'unlimited' ? '∞' : limits.gap}`}</Field>
                        <Field label="Contacts found">{`${u.contact_finder_count ?? 0} / ${limits.contact === 'unlimited' ? '∞' : limits.contact}`}</Field>
                        <Field label="Email limit">{u.email_limit ?? '—'}</Field>
                        <RowLimitEditor
                          key={u.id}
                          user={u}
                          saving={updating}
                          onSave={(next) => updateUser(u.id, { row_limit: next })}
                        />
                        <SeatLimitEditor
                          key={`seats-${u.id}`}
                          user={u}
                          saving={updating}
                          onSave={(next) => updateUser(u.id, { seat_limit: next })}
                        />
                      </dl>
                    </Section>

                    <Section title="Vertical access" icon={Layers}>
                      {verticalAccessLoading && !verticalAccess && (
                        <p className="text-xs text-gray-500 dark:text-slate-400">Loading…</p>
                      )}
                      {verticalAccessError && (
                        <p className="text-xs text-red-600 dark:text-red-400 mb-2">{verticalAccessError}</p>
                      )}
                      {verticalAccess && (
                        <div className="space-y-2">
                          {[...verticalAccess.verticals]
                            .sort((a, b) => (a.vertical === 'Trading & Investment' ? -1 : b.vertical === 'Trading & Investment' ? 1 : 0))
                            .map((item) => (
                              <VerticalAccessRow
                                key={item.vertical}
                                item={item}
                                isForex={item.vertical === 'Trading & Investment'}
                                saving={!!verticalAccessSaving[item.vertical]}
                                onToggle={(vertical, next) => toggleVerticalAccess(u.id, vertical, next)}
                              />
                            ))}
                        </div>
                      )}
                    </Section>

                    {detail && (
                      <Section title={`Brands (${detail.brands.length})`} icon={Building2}>
                        <div className="flex gap-4 mb-3 text-xs text-gray-600 dark:text-slate-400">
                          <span>{detail.brandCounts.projects} projects</span>
                          <span>{detail.brandCounts.competitors} competitors</span>
                          <span>{detail.brandCounts.gaps} gaps</span>
                        </div>
                        {detail.brands.length === 0 ? (
                          <p className="text-sm text-gray-500 dark:text-slate-400">No brands on this account.</p>
                        ) : (
                          <div className="max-h-56 overflow-y-auto rounded-lg border border-gray-200 dark:border-slate-700 divide-y divide-gray-100 dark:divide-slate-800">
                            {detail.brands.map((b) => (
                              <div key={b.id} className="px-3 py-2 flex items-center justify-between gap-3 text-sm">
                                <div className="min-w-0">
                                  <div className="text-gray-900 dark:text-slate-100 truncate">{b.brand_name || b.brand_url}</div>
                                  <div className="text-xs text-gray-500 dark:text-slate-400 truncate">{b.brand_url}</div>
                                </div>
                                <div className="shrink-0 text-xs text-gray-500 dark:text-slate-400 text-right">
                                  <div>{b.gap_brand === 'Yes' ? 'gap' : b.comp_brand === 'Yes' ? 'competitor' : 'project'}</div>
                                  <div>{b.processing_status || '—'}</div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </Section>
                    )}

                    {detail && (
                      <Section title={`Sign-in history (${detail.logins?.length || 0})`} icon={Globe}>
                        {!detail.logins?.length ? (
                          <p className="text-sm text-gray-500 dark:text-slate-400">
                            No logins recorded. Only sign-ins since IP logging was added appear here; an
                            admin using "Login as" is deliberately not recorded.
                          </p>
                        ) : (
                          <div className="max-h-56 overflow-y-auto rounded-lg border border-gray-200 dark:border-slate-700 divide-y divide-gray-100 dark:divide-slate-800">
                            {detail.logins.map((entry, i) => (
                              <div key={i} className="px-3 py-2 flex items-start justify-between gap-3 text-sm">
                                <div className="min-w-0">
                                  <CopyField value={entry.ip || 'unknown IP'} />
                                  <div className="text-xs text-gray-500 dark:text-slate-400 truncate" title={entry.user_agent || ''}>
                                    {entry.user_agent || 'no user agent'}
                                  </div>
                                </div>
                                <div className="shrink-0 text-xs text-gray-500 dark:text-slate-400 text-right">
                                  <div>{entry.method || 'unknown'}</div>
                                  <div>{fmtDateTime(entry.created_at)}</div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </Section>
                    )}

                    {detail && (detail.subscriptions.length > 0 || detail.stripeSubscriptions.length > 0) && (
                      <Section title="Subscription history" icon={CreditCard}>
                        <div className="rounded-lg border border-gray-200 dark:border-slate-700 divide-y divide-gray-100 dark:divide-slate-800">
                          {detail.subscriptions.map((s, i) => (
                            <div key={`us-${i}`} className="px-3 py-2 text-sm">
                              <div className="flex items-center justify-between gap-3">
                                <span className="font-mono text-xs truncate">{s.subscription_id}</span>
                                <span className="text-xs text-gray-500 dark:text-slate-400">{s.status}</span>
                              </div>
                              <div className="text-xs text-gray-500 dark:text-slate-400 mt-0.5">
                                {s.plan_nickname || '—'} · {fmtMoney(s.plan_amount, s.plan_currency)}
                                {s.plan_interval ? ` / ${s.plan_interval}` : ''} · {fmtDate(s.current_period_start)} → {fmtDate(s.current_period_end)}
                                {s.cancel_at_period_end ? ' · cancels at period end' : ''}
                              </div>
                            </div>
                          ))}
                          {detail.stripeSubscriptions.map((s, i) => (
                            <div key={`ss-${i}`} className="px-3 py-2 text-sm">
                              <div className="flex items-center justify-between gap-3">
                                <span className="font-mono text-xs truncate">{s.stripe_subscription_id}</span>
                                <span className="text-xs text-gray-500 dark:text-slate-400">{s.status}</span>
                              </div>
                              <div className="text-xs text-gray-500 dark:text-slate-400 mt-0.5">
                                {s.plan_name || '—'} · {fmtDate(s.current_period_start)} → {fmtDate(s.current_period_end)}
                              </div>
                            </div>
                          ))}
                        </div>
                      </Section>
                    )}

                    {detail && detail.payments.length > 0 && (
                      <Section title={`Payments (${detail.payments.length})`} icon={CreditCard}>
                        <div className="max-h-56 overflow-y-auto rounded-lg border border-gray-200 dark:border-slate-700 divide-y divide-gray-100 dark:divide-slate-800">
                          {detail.payments.map((p, i) => (
                            <div key={i} className="px-3 py-2 flex items-center justify-between gap-3 text-sm">
                              <div className="min-w-0">
                                <div className="text-gray-900 dark:text-slate-100">{fmtMoney(p.amount, p.currency)}</div>
                                <div className="text-xs text-gray-500 dark:text-slate-400 truncate">
                                  {p.description || p.stripe_invoice_id || p.stripe_payment_intent_id}
                                </div>
                              </div>
                              <div className="shrink-0 text-xs text-gray-500 dark:text-slate-400 text-right">
                                <div>{p.status}</div>
                                <div>{fmtDate(p.created_at)}</div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </Section>
                    )}

                    <Section title="Consent & legal" icon={ShieldCheck}>
                      <dl className="grid grid-cols-2 gap-x-4 gap-y-3">
                        <Field label="Terms version">{u.terms_version || '—'}</Field>
                        <Field label="Terms accepted">{fmtDate(u.terms_accepted_at)}</Field>
                        <Field label="Accepted from IP">{u.terms_accepted_ip || '—'}</Field>
                        <Field label="Marketing opt-in">
                          {u.marketing_opt_in ? `Yes · ${fmtDate(u.marketing_opt_in_at)}` : 'No'}
                        </Field>
                      </dl>
                    </Section>
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserPanel;